/* global BigInt */
/**
 * POPUP: AddInventoryCountPopup.jsx
 * Pilcore Purchasing - Start and review inventory counts
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import * as XLSX from "xlsx";
import { FiDownload, FiRefreshCw, FiUpload, FiX } from "react-icons/fi";
import { uploadCountWorkbook, isCountWorkbookFile } from "../inventoryCountWorkbookApi";
import axios from "../../../api/axiosInstance";
import {
  INVENTORY_COUNT_UI_STYLES,
  buildInventoryCountHierarchy,
  formatInventoryCountDate,
  formatInventoryCountInput,
  formatInventoryCountNumber,
  getInventoryCountStatusMeta,
  truncateInventoryCountText,
} from "../inventoryCountUi";
import { inventoryBasis, exactInput, historicalExact, quantityFromExact, displayQuantity, stockInUnit } from "../../utils/inventoryUnitQuantity";
import { isDirectConsumptionItem } from "../itemsFamiliesUi";
import { usePmsTheme } from "../../../Components/PmsThemeContext";
import useInventoryCountAccess from "../useInventoryCountAccess";
import { inventoryCountReferenceText } from "./inventoryCountReferenceMessages";
import InventoryCountFinalizationReview from "./InventoryCountFinalizationReview";
import InventoryCountStartGuard from "./InventoryCountStartGuard";
import InventoryCountScopePicker from "./InventoryCountScopePicker";
import InventoryCountActions from "./InventoryCountActions";
import "../../PurchasingUi.css";
import "./InventoryCountReference.css";

function getConfirmButtonClass(tone) {
  if (tone === "danger") return "ic-btn ic-btn--danger";
  if (tone === "warning") return "ic-btn ic-btn--warning";
  return "ic-btn ic-btn--primary";
}

function InventoryCountConfirmDialog({ action, busy, onCancel, onConfirm, t }) {
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  useEffect(() => {
    setCheckboxChecked(Boolean(action?.checkbox?.defaultChecked));
  }, [action]);

  if (!action) return null;

  const choices = Array.isArray(action.choices) ? action.choices : [];
  const confirmClass = getConfirmButtonClass(action.tone);
  const checkbox = action.checkbox;
  const confirmationDisabled = busy || Boolean(checkbox?.required && !checkboxChecked);

  return (
    <div
      className="ic-dialogOverlay"
      onMouseDown={(event) => {
        if (!busy && event.target === event.currentTarget) {
          onCancel();
        }
      }}
    >
      <div className="ic-dialog" role="alertdialog" aria-modal="true" aria-label={t(action.title)} tabIndex={-1}>
        <h3 className="ic-dialogTitle">{t(action.title)}</h3>
        <p className="ic-dialogText">{t(action.message)}</p>
        {action.stockNotice && <p role="status" className="ic-dialogText">{t("Stock changed on the marked lines. Review them before confirming.")}</p>}
        {checkbox ? (
          <label className="ic-dialogCheckbox">
            <input
              type="checkbox"
              checked={checkboxChecked}
              onChange={(event) => setCheckboxChecked(event.target.checked)}
              disabled={busy}
            />
            <span>
              <strong>{t(checkbox.label)}</strong>
              {checkbox.helper ? <small>{t(checkbox.helper)}</small> : null}
            </span>
          </label>
        ) : null}
        <div className="ic-dialogActions">
          {!action.noticeOnly && <button type="button" className="ic-btn ic-btn--ghost" onClick={onCancel} disabled={busy}>
            {t("Cancel")}
          </button>}
          {choices.length ? (
            choices.map((choice) => (
              <button
                key={choice.value}
                type="button"
                className={getConfirmButtonClass(choice.tone || action.tone)}
                onClick={() => onConfirm(choice.value, checkboxChecked)}
                disabled={confirmationDisabled}
              >
                {busy ? t("Working...") : t(choice.label)}
              </button>
            ))
          ) : (
            <button
              type="button"
              className={confirmClass}
              onClick={() => onConfirm(undefined, checkboxChecked)}
              disabled={confirmationDisabled}
            >
              {busy ? t("Working...") : t(action.confirmLabel)}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function restoreLineUnitState(line) {
  const item = line.item || {};
  const recordedUnitLabel = line.count_unit?.name || null;
  const persistedFactor = Number(
    line.count_unit_factor || line.__conversionFactor || 0
  );
  const factorFor = (fallback) =>
    Number.isFinite(persistedFactor) && persistedFactor > 0
      ? persistedFactor
      : fallback;

  if (line.count_unit_used === "unit" && (item.unit || recordedUnitLabel)) {
    return {
      selectedUnit: { key: "unit", label: recordedUnitLabel || item.unit?.name },
      factor: factorFor(1),
    };
  }

  if (line.count_unit_used === "subunit" && (item.subunit || recordedUnitLabel)) {
    return {
      selectedUnit: { key: "subunit", label: recordedUnitLabel || item.subunit?.name },
      factor: factorFor(item.pack_size || 1),
    };
  }

  if (line.count_unit_used === "subunit2" && (item.subunit2 || recordedUnitLabel)) {
    return {
      selectedUnit: { key: "subunit2", label: recordedUnitLabel || item.subunit2?.name },
      factor: factorFor((item.pack_size || 1) * (item.subunit2_size || 1)),
    };
  }

  if (item.unit) {
    return {
      selectedUnit: { key: "unit", label: item.unit.name },
      factor: 1,
    };
  }

  return {
    selectedUnit: null,
    factor: 1,
  };
}

function buildUnitOptions(line) {
  const item = line.item || {};
  const options = [];

  if (item.unit) {
    options.push({
      key: "unit",
      unitId: item.unit.id,
      label: item.unit.name,
      factor: 1,
      helper: "Base count unit",
    });
  }

  if (item.subunit) {
    const factor = item.pack_size || 1;
    options.push({
      key: "subunit",
      unitId: item.subunit.id,
      label: item.subunit.name,
      factor,
      helper: item.unit
        ? `1 ${item.unit.name} = ${formatInventoryCountNumber(factor)} ${item.subunit.name}`
        : `${formatInventoryCountNumber(factor)} base units`,
    });
  }

  if (item.subunit2) {
    const factor = (item.pack_size || 1) * (item.subunit2_size || 1);
    options.push({
      key: "subunit2",
      unitId: item.subunit2.id,
      label: item.subunit2.name,
      factor,
      helper: item.unit
        ? `1 ${item.unit.name} = ${formatInventoryCountNumber(factor)} ${item.subunit2.name}`
        : `${formatInventoryCountNumber(factor)} base units`,
    });
  }

  return options;
}

function buildCorrectionUnitOptions(line) {
  const options = buildUnitOptions(line);
  const recordedKey = line.__selectedUnit?.key || line.count_unit_used || null;
  const recordedLabel = line.__selectedUnit?.label || line.count_unit?.name || null;
  const recordedUnitId = Number(line.count_unit_id || line.count_unit?.id || 0) || null;
  const recordedFactor = Number(line.__conversionFactor || line.count_unit_factor || 0);
  if (!recordedKey || !recordedLabel || !(recordedFactor > 0)) return options;

  const recordedOption = {
    key: recordedKey,
    unitId: recordedUnitId,
    label: recordedLabel,
    factor: recordedFactor,
    helper: `Recorded conversion: ${formatInventoryCountNumber(recordedFactor)} per inventory unit`,
    recorded: true,
  };
  const matchingIndex = options.findIndex((option) => option.key === recordedKey);
  if (matchingIndex < 0) return [recordedOption, ...options];

  return options.map((option, index) =>
    index === matchingIndex ? recordedOption : option
  );
}

function getLineUnitLabel(line) {
  const item = line.item || {};
  return (
    line.__selectedUnit?.label ||
    item.unit?.name ||
    item.subunit?.name ||
    item.subunit2?.name ||
    "-"
  );
}

function toIdArray(value) {
  return Array.isArray(value)
    ? value.map((entry) => Number(entry)).filter((entry) => Number.isFinite(entry))
    : [];
}

function isMainCountLocation(location, context = {}) {
  return Boolean(location.is_main) || String(location.code || "").trim().toUpperCase() === "MAIN"
    || (context.main_location_id != null && Number(location.id) === Number(context.main_location_id));
}

function normalizeCountStartContext(context) {
  const hasOtherLocations = context.has_non_main_locations || context.locations.some((location) => !isMainCountLocation(location, context));
  return { ...context, has_non_main_locations: Boolean(hasOtherLocations), locations: context.locations.filter((location) =>
    !isMainCountLocation(location, context) || (!hasOtherLocations && context.has_non_main_locations === false)) };
}

function soleCountLocation(context) {
  if (context.locations.length !== 1) return [];
  const [location] = context.locations;
  const main = isMainCountLocation(location, context);
  return main && context.has_non_main_locations !== false ? [] : [Number(location.id)];
}

function getLocationCountKey(lineId, locationId) {
  return `${lineId}:${locationId}`;
}

function getLineLocationLines(line) {
  return Array.isArray(line?.location_lines) ? line.location_lines : [];
}

function isBlankCountValue(value) {
  return value === null || value === undefined || String(value).trim() === "";
}

function countLevel(line) {
  return line?.__selectedUnit?.key || line?.count_unit_used || "unit";
}

function countDisplay(line, value, level = countLevel(line), exact) {
  return displayQuantity(value, level, countBasis(line), exact);
}

function countBasis(line) {
  return line?.physical_basis || line?.counted_exact_quantity || line?.expected_exact_quantity || inventoryBasis(line?.item);
}

function countNumber(line, value, level = countLevel(line)) {
  return formatInventoryCountNumber(countDisplay(line, value, level));
}

function expectedInUnit(line, value, factor, level = countLevel(line), exact = null) {
  return Number(stockInUnit(value ?? 0, level, countBasis(line), exact, factor));
}

function savedCountExact(line, cell = line) {
  const basis = countBasis(line);
  if (!basis || isBlankCountValue(cell.counted_qty)) return null;
  const factor = Number(line.__conversionFactor || line.count_unit_factor || 1);
  return cell.counted_exact_quantity || exactInput(cell.counted_qty, countLevel(line), basis) ||
    historicalExact(Number(cell.counted_qty) / factor, basis);
}

function exactCountValue(line, cell, value) {
  return String(value) === String(cell?.counted_qty) ? savedCountExact(line, cell) : exactInput(value, countLevel(line), countBasis(line));
}

function countDifference(line, cell, counted, expected) {
  const basis = countBasis(line);
  if (!basis) return Number(counted) - expected;
  const physicalExpected = cell?.expected_exact_quantity || historicalExact(cell?.expected_qty, basis);
  const physicalCount = exactCountValue(line, cell, counted);
  if (!physicalExpected || !physicalCount) return Number(counted) - expected;
  const delta = { ...physicalCount, steps: String(BigInt(physicalCount.steps) - BigInt(physicalExpected.steps)) };
  return Number(quantityFromExact(delta, countLevel(line), 24));
}

function normalizeCountLine(line) {
  const { selectedUnit, factor } = restoreLineUnitState(line);
  const normalizeCount = (cell) => {
    const physical = savedCountExact(line, cell);
    return { ...cell, counted_exact_quantity: physical,
      counted_qty: formatInventoryCountInput(quantityFromExact(physical, selectedUnit?.key) ?? cell.counted_qty) };
  };
  return { ...normalizeCount(line), location_lines: getLineLocationLines(line).map(normalizeCount),
    __selectedUnit: selectedUnit, __conversionFactor: factor };
}

function mergeCountUnitResponse(line, savedLine, payload, option) {
  const sameUnit = line.count_unit_used === option.key;
  const hasSaved = (key) => Object.prototype.hasOwnProperty.call(savedLine || {}, key);
  const unitId = hasSaved("count_unit_id") ? savedLine.count_unit_id
    : sameUnit ? line.count_unit_id || option.unitId || null : option.unitId || null;
  const matchingUnit = (unit) => unitId != null && Number(unit?.id) === Number(unitId);
  const recordedUnit = matchingUnit(savedLine?.count_unit) ? savedLine.count_unit
    : sameUnit && matchingUnit(line.count_unit) ? line.count_unit : null;
  const factor = hasSaved("count_unit_factor") ? Number(savedLine.count_unit_factor) > 0 ? Number(savedLine.count_unit_factor) : option.factor
    : sameUnit && Number(line.count_unit_factor) > 0 ? Number(line.count_unit_factor) : option.factor;
  return normalizeCountLine({
    ...line, ...savedLine,
    // PATCH returns the item's units, while GET also includes its product group.
    item: { ...line.item, ...savedLine?.item },
    ...(!savedLine && !payload.location_counts ? { counted_qty: payload.counted_qty,
      counted_exact_quantity: payload.counted_exact_quantity ?? null } : {}),
    count_unit_used: option.key,
    count_unit_id: unitId,
    count_unit_factor: hasSaved("count_unit_factor") ? savedLine.count_unit_factor : factor,
    count_unit: recordedUnit,
    // Infer missing exact quantities using the confirmed unit, not old UI state.
    __selectedUnit: { key: option.key, label: recordedUnit?.name || option.label },
    __conversionFactor: factor,
  });
}

function compatibleExpectedScope(session, lines, fresh) {
  const scope = (record) => JSON.stringify([record.blind_mode === true, record.snapshot_date,
    ...["visible_location_ids", "selected_locations"].map((key) => toIdArray(record[key]).sort((a, b) => a - b))]);
  if (scope(session) !== scope(fresh)) return false;
  const units = (line) => {
    const { selectedUnit, factor } = restoreLineUnitState(line);
    const key = selectedUnit?.key;
    return JSON.stringify([line.item_id, key, factor, line.count_unit_id ?? line.item?.[key]?.id ?? line.item?.[`${key}_id`],
      line.item?.unit_id, line.item?.subunit_id, line.item?.subunit2_id, line.item?.pack_size, line.item?.subunit2_size]);
  };
  const byId = new Map(fresh.lines.map((line) => [Number(line.id), line]));
  return lines.every((line) => {
    const next = byId.get(Number(line.id));
    if (!next || units(line) !== units(next)) return false;
    const cells = new Map(getLineLocationLines(next).map((cell) => [Number(cell.location_id), cell.id]));
    return getLineLocationLines(line).every((cell) => cells.has(Number(cell.location_id)) && cells.get(Number(cell.location_id)) === cell.id);
  });
}

function mergeExpectedStock(lines, freshLines) {
  const byId = new Map(lines.map((line) => [Number(line.id), line]));
  return freshLines.map((fresh) => {
    const line = byId.get(Number(fresh.id));
    if (!line) return { ...normalizeCountLine(fresh), expected_changed: true };
    const oldCells = new Map(getLineLocationLines(line).map((cell) => [Number(cell.location_id), cell]));
    const locationLines = getLineLocationLines(fresh).map((next) => {
      const cell = oldCells.get(Number(next.location_id));
      if (!cell) return { ...next, counted_qty: formatInventoryCountInput(quantityFromExact(next.counted_exact_quantity, countLevel(line)) ?? next.counted_qty), expected_changed: true };
      return { ...cell, expected_qty: next.expected_qty, expected_exact_quantity: next.expected_exact_quantity, reference_expected_qty: next.reference_expected_qty,
        expected_changed: Boolean(next.expected_changed || cell.expected_changed || Number(next.expected_qty) !== Number(cell.expected_qty)) };
    });
    return { ...line, expected_qty: fresh.expected_qty, expected_exact_quantity: fresh.expected_exact_quantity, reference_expected_qty: fresh.reference_expected_qty, expected_revision: fresh.expected_revision,
      expected_changed: Boolean(line.expected_changed || fresh.expected_changed || locationLines.some((cell) => cell.expected_changed) ||
        (line.expected_revision && fresh.expected_revision && line.expected_revision !== fresh.expected_revision) ||
        Number(fresh.expected_qty) !== Number(line.expected_qty)),
      location_lines: locationLines };
  });
}

function markReviewedStock(preview, lines, previousReview) {
  const byItem = new Map(lines.map((line) => [Number(line.item_id), line]));
  const priorRows = new Map((previousReview?.rows || []).map((row) => [Number(row.item_id), row]));
  return { ...preview, rows: preview.rows.map((row) => {
    const line = byItem.get(Number(row.item_id));
    const prior = priorRows.get(Number(row.item_id));
    const previousRevision = prior?.expected_revision || line?.expected_revision;
    return { ...row, physical_basis: row.physical_basis || countBasis(line), expected_changed: Boolean(row.expected_changed || !line || line.expected_changed || prior?.expected_changed ||
      (previousRevision && row.expected_revision && previousRevision !== row.expected_revision)) };
  }) };
}

function normalizeInventoryCountSearchText(value) {
  return String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .trim();
}

function PilcoreInventoryCountPopup(props) {
  const { themeId, languageId } = usePmsTheme();
  return <InventoryCountContent key={props.businessUnitId} {...props} uiTheme={themeId} uiLanguage={languageId} />;
}

export default function AddInventoryCountPopup(props) {
  return props.pilcoreAppearance
    ? <PilcoreInventoryCountPopup {...props} />
    : <InventoryCountContent key={props.businessUnitId} {...props} />;
}

function InventoryCountContent({
  isOpen,
  onClose,
  businessUnitId,
  sessionIdFromList,
  autoPromptFinalize = false,
  pilcoreAppearance = false,
  uiTheme = "light",
  uiLanguage = "en",
}) {
  const { can, requestConfig, contextKey } = useInventoryCountAccess(businessUnitId);
  const t = useCallback((value) => inventoryCountReferenceText(uiLanguage, value), [uiLanguage]);
  const appearanceRef = useRef(null);
  const pendingConfirmationRef = useRef(null);
  const closeTopDialogRef = useRef(null);
  const [noticeAction, setNoticeAction] = useState(null);
  const showNotice = (message, waitForDismiss = false) => {
    if (!pilcoreAppearance) return window.alert(message);
    setNoticeAction({ title: "Inventory Count", message, confirmLabel: "Close", noticeOnly: true });
    if (waitForDismiss) {
      return new Promise((resolve) => { pendingConfirmationRef.current = resolve; });
    }
  };
  const requestConfirmation = (message) => {
    if (!pilcoreAppearance) return Promise.resolve(window.confirm(message));
    return new Promise((resolve) => {
      pendingConfirmationRef.current = resolve;
      setNoticeAction({ title: "Confirm changes", message, confirmLabel: "Confirm", tone: "danger" });
    });
  };
  const dismissNotice = (confirmed = false) => {
    pendingConfirmationRef.current?.(confirmed);
    pendingConfirmationRef.current = null;
    setNoticeAction(null);
  };
  useEffect(() => () => pendingConfirmationRef.current?.(false), []);
  const [loading, setLoading] = useState(false);
  const [contentReady, setContentReady] = useState(false);
  const [sessionLoadError, setSessionLoadError] = useState("");
  const [bootstrapRevision, setBootstrapRevision] = useState(0);
  const [items, setItems] = useState([]);
  const [inventoryLocations, setInventoryLocations] = useState([]);
  const [startContext, setStartContext] = useState(null);
  const [startContextError, setStartContextError] = useState(false);
  const [startPolicy, setStartPolicy] = useState(null);
  const startRequestRef = useRef(false);
  const startRequestConfig = useCallback(() => requestConfig("create"), [requestConfig]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [scopeCatalog, setScopeCatalog] = useState({ families: [], subfamilies: [], categories: [], subcategories: [] });
  const [scopeFilters, setScopeFilters] = useState({ families: [], subfamilies: [], categories: [], subcategories: [] });
  const [scopePickerOpen, setScopePickerOpen] = useState(false);
  const [activeCountLocationId, setActiveCountLocationId] = useState(null);
  const [addedLocationIds, setAddedLocationIds] = useState([]);
  const [locationFeedback, setLocationFeedback] = useState(null);
  useEffect(() => {
    if (!locationFeedback) return undefined;
    const timeout = window.setTimeout(() => setLocationFeedback(null), 1500);
    return () => window.clearTimeout(timeout);
  }, [locationFeedback]);
  const [sessionId, setSessionId] = useState(null);
  const [session, setSession] = useState(null);
  const [lines, setLines] = useState([]);
  const [liveCounted, setLiveCounted] = useState({});
  const [liveLocationCounts, setLiveLocationCounts] = useState({});
  const [snapshotDate, setSnapshotDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [refreshStatus, setRefreshStatus] = useState("idle");
  const refreshingExpectedRef = useRef(false);
  const draftRef = useRef(null);
  const [refreshRecovery, setRefreshRecovery] = useState(null);
  const [showRefreshedFinalizePrompt, setShowRefreshedFinalizePrompt] = useState(false);
  const [countSearch, setCountSearch] = useState("");
  const [countViewFilter, setCountViewFilter] = useState("all");
  const [countSaveState, setCountSaveState] = useState("idle");
  const [closeSaveBusy, setCloseSaveBusy] = useState(false);
  const closeSavePendingRef = useRef(false);
  const [workbookAction, setWorkbookAction] = useState(null);
  const workbookFileRef = useRef(null);
  const countSaveQueuesRef = useRef(new Map());
  const failedCountSavesRef = useRef(new Set());
  const pendingCountSavesRef = useRef(0);
  const countSaveGenerationRef = useRef(0);
  const [blindMode, setBlindMode] = useState(false);
  const hideDerivedCountFigures = blindMode;
  const [showPastItemsPopup, setShowPastItemsPopup] = useState(false);
  const [pastItems, setPastItems] = useState([]);
  const [pastItemsSearch, setPastItemsSearch] = useState("");
  const [addingPastItemId, setAddingPastItemId] = useState(null);
  const [showUnitModal, setShowUnitModal] = useState(false);
  const [unitModalItem, setUnitModalItem] = useState(null);
  const [confirmAction, setConfirmAction] = useState(null);
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [finalizationReview, setFinalizationReview] = useState(null);
  const [finalizationBusy, setFinalizationBusy] = useState(false);
  const finalizationPendingRef = useRef(false);
  const [correctionLine, setCorrectionLine] = useState(null);
  const [correctionValues, setCorrectionValues] = useState({});
  const [correctionExact, setCorrectionExact] = useState({});
  const [correctionEditKey, setCorrectionEditKey] = useState(null);
  const [correctionUnit, setCorrectionUnit] = useState(null);
  const [correctionReason, setCorrectionReason] = useState("");
  const [correctionBusy, setCorrectionBusy] = useState(false);
  const [locationsEditOpen, setLocationsEditOpen] = useState(false);
  const [locationSearch, setLocationSearch] = useState("");
  const [structureBusy, setStructureBusy] = useState(false);
  const syncingLocationScrollRef = useRef(false);
  const locationsEditSnapshotRef = useRef(null);
  const autoPromptFinalizeSessionRef = useRef(null);
  const sessionLoadRevisionRef = useRef(0);

  const today = new Date().toISOString().slice(0, 10);

  const syncLocationScroll = (event) => {
    if (syncingLocationScrollRef.current) return;

    const source = event.currentTarget;
    const scrollContainer = source.closest(".ic-locationScrollSyncScope");
    if (!scrollContainer) return;

    syncingLocationScrollRef.current = true;
    const { scrollLeft } = source;

    scrollContainer.querySelectorAll(".ic-locationScroll").forEach((target) => {
      if (target !== source) {
        target.scrollLeft = scrollLeft;
      }
    });

    window.requestAnimationFrame(() => {
      syncingLocationScrollRef.current = false;
    });
  };

  const resetDraftState = useCallback(() => {
    setStartContext(null);
    setStartContextError(false);
    setStartPolicy(null);
    startRequestRef.current = false;
    setSessionId(null);
    setSession(null);
    setLines([]);
    setLiveCounted({});
    setLiveLocationCounts({});
    setSelectedLocations([]);
    setScopeCatalog({ families: [], subfamilies: [], categories: [], subcategories: [] });
    setScopeFilters({ families: [], subfamilies: [], categories: [], subcategories: [] });
    setScopePickerOpen(false);
    setActiveCountLocationId(null);
    setAddedLocationIds([]);
    setLocationFeedback(null);
    setSnapshotDate(new Date().toISOString().slice(0, 10));
    setNotes("");
    setCountSearch("");
    setCountViewFilter("all");
    setBlindMode(false);
    setShowPastItemsPopup(false);
    setPastItems([]);
    setPastItemsSearch("");
    setAddingPastItemId(null);
    setShowUnitModal(false);
    setUnitModalItem(null);
    setConfirmAction(null);
    setCorrectionLine(null);
    setCorrectionValues({});
    setCorrectionUnit(null);
    setCorrectionReason("");
    setCorrectionBusy(false);

    setLocationsEditOpen(false);
    setStructureBusy(false);
  }, []);

  const loadAll = useCallback(async (shouldApply = () => true) => {
    try {
      const [
        itemsResponse,
        locationsResponse,
        ...scopeResponses
      ] =
        await Promise.all([
          axios.get(`/api/purchasing/items?business_unit_id=${businessUnitId}`, { ...requestConfig(), params: { status: "all" } }),
          sessionIdFromList
            ? axios.get("/api/purchasing/inventory/locations?include_inactive=false", requestConfig()).catch(() => ({ data: [] }))
            : axios.get("/api/purchasing/inventory-counts/start-context", { ...startRequestConfig(), params: { business_unit_id: businessUnitId } })
              .catch(() => ({ data: null })),
          ...(!sessionIdFromList ? ["families", "subfamilies", "categories", "subcategories"].map((level) =>
            axios.get(`/api/purchasing/${level}?business_unit_id=${businessUnitId}`, requestConfig())) : []),
        ]);

      if (!shouldApply() || !can("view", true)) return;
      if (!sessionIdFromList) {
        if (scopeResponses.some((response) => !Array.isArray(response.data))) throw new Error("Invalid count scope catalog");
        setScopeCatalog(Object.fromEntries(["families", "subfamilies", "categories", "subcategories"].map((level, index) => [level, scopeResponses[index].data])));
      }
      setItems((itemsResponse.data || []).filter((item) => !isDirectConsumptionItem(item)));
      const receivedContext = !sessionIdFromList && locationsResponse.data;
      const validContext = receivedContext && Array.isArray(receivedContext.locations) && typeof receivedContext.has_non_main_locations === "boolean";
      const context = validContext ? normalizeCountStartContext(receivedContext) : null;
      const locations = sessionIdFromList ? locationsResponse.data || [] : context?.locations || [];
      if (!sessionIdFromList) {
        setStartContext(validContext ? context : null);
        setStartContextError(!validContext);
      }
      setInventoryLocations(locations);
      if (validContext) setSelectedLocations((current) => {
        const available = new Set(context.locations.map((location) => Number(location.id)));
        const retained = current.filter((id) => available.has(Number(id)));
        return retained.length ? retained : soleCountLocation(context);
      });
    } catch (error) {
      console.error("[INV-COUNT][LOAD BASE][ERROR]", error);
      if (shouldApply() && !sessionIdFromList) {
        setStartContext(null);
        setStartContextError(true);
      }
    }
  }, [businessUnitId, sessionIdFromList, startRequestConfig, can, requestConfig]);

  const fetchSession = useCallback(async (id) => {
    const res = await axios.get(`/api/purchasing/inventory-counts/${id}`, requestConfig());
    if (Number(res.data?.id) !== Number(id) || !Array.isArray(res.data?.lines)) {
      throw new Error("Invalid inventory count details");
    }
    return res.data;
  }, [requestConfig]);

  const applySession = useCallback((nextSession, id) => {
    setSession(nextSession);
    setSessionId(nextSession.id || id);
    setSnapshotDate(nextSession.snapshot_date ? String(nextSession.snapshot_date).slice(0, 10) : today);
    setNotes(nextSession.notes || "");
    setBlindMode(Boolean(nextSession.blind_mode));
    setLiveCounted({});
    setLiveLocationCounts({});

    setLines(nextSession.lines.map(normalizeCountLine));
    setSelectedLocations(toIdArray(nextSession.selected_locations));
  }, [today]);

  const loadSession = useCallback(async (id, generation = countSaveGenerationRef.current) => {
    if (generation !== countSaveGenerationRef.current) return null;
    const requestRevision = ++sessionLoadRevisionRef.current;
    const nextSession = await fetchSession(id);
    if (sessionLoadRevisionRef.current !== requestRevision || generation !== countSaveGenerationRef.current) return null;
    applySession(nextSession, id);
    return nextSession;
  }, [applySession, fetchSession]);

  useEffect(() => {
    const requestRevision = ++sessionLoadRevisionRef.current;
    if (!isOpen || !can("view") || (!sessionIdFromList && !can("create"))) return;

    let cancelled = false;

    const bootstrap = async () => {
      setContentReady(false);
      setSessionLoadError("");
      setLoading(false);
      setConfirmAction(null);
      pendingConfirmationRef.current?.(false);
      pendingConfirmationRef.current = null;
      setNoticeAction(null);
      setConfirmBusy(false);
      setCorrectionLine(null);
      setCorrectionBusy(false);
      setFinalizationReview(null);
      setFinalizationBusy(false);
      finalizationPendingRef.current = false;
      autoPromptFinalizeSessionRef.current = null;
      setPastItems([]);
      setPastItemsSearch("");
      setCountSearch("");
      setCountViewFilter("all");
      setCountSaveState("idle");
      setCloseSaveBusy(false);
      closeSavePendingRef.current = false;
      refreshingExpectedRef.current = false;
      setRefreshStatus("idle");
      setRefreshRecovery(null);
      setShowRefreshedFinalizePrompt(false);
      setWorkbookAction(null);
      countSaveGenerationRef.current += 1;
      pendingCountSavesRef.current = 0;
      countSaveQueuesRef.current.clear();
      failedCountSavesRef.current.clear();
      setAddingPastItemId(null);
      setShowPastItemsPopup(false);
      setShowUnitModal(false);
      setUnitModalItem(null);

      setLocationsEditOpen(false);
      setScopePickerOpen(false);
      setStructureBusy(false);
      setAddedLocationIds([]);
      setLocationFeedback(null);

      if (!sessionIdFromList) {
        resetDraftState();
      } else {
        // Never leave a previous/draft workspace visible while a requested
        // review session is loading.
        setSessionId(null);
        setSession(null);
      }

      try {
        await loadAll(
          () => !cancelled && sessionLoadRevisionRef.current === requestRevision
        );
        if (cancelled || sessionLoadRevisionRef.current !== requestRevision) return;

        if (sessionIdFromList) {
          const nextSession = await fetchSession(sessionIdFromList);
          if (cancelled || sessionLoadRevisionRef.current !== requestRevision) return;
          applySession(nextSession, sessionIdFromList);
        }

        if (!cancelled && sessionLoadRevisionRef.current === requestRevision) {
          setContentReady(true);
        }
      } catch (error) {
        console.error("[INV-COUNT][LOAD SESSION][ERROR]", error);
        if (!cancelled && sessionLoadRevisionRef.current === requestRevision) {
          setSessionLoadError(
            error?.response?.data?.error ||
              error?.response?.data?.message ||
              "The inventory count could not be loaded."
          );
          setContentReady(true);
        }
      }
    };

    bootstrap();

    return () => {
      cancelled = true;
      countSaveGenerationRef.current += 1;
      sessionLoadRevisionRef.current += 1;
    };
  }, [
    businessUnitId,
    contextKey,
    can,
    bootstrapRevision,
    applySession,
    fetchSession,
    isOpen,
    loadAll,
    resetDraftState,
    sessionIdFromList,
  ]);

  const sessionStatusMeta = getInventoryCountStatusMeta(session?.status);
  const sessionStatusKey = sessionStatusMeta.key;
  const isCounting = Boolean(sessionId) && ["open", "in_progress", "reverted"].includes(sessionStatusKey);
  const canEditSession = can("update") && isCounting;
  const canCorrectFinalizedLines = can("correct") && Boolean(sessionId) && sessionStatusKey === "finalized";
  const canFinalizeSession = can("finalize") && isCounting;
  const canDeleteSession = can("delete") && Boolean(sessionId) && sessionStatusKey === "open";
  const activeLocations = session?.selected_locations || selectedLocations;
  const selectableLocations = useMemo(() => {
    if (!sessionId) return inventoryLocations;
    const hasOtherLocations = inventoryLocations.some((location) => !isMainCountLocation(location));
    const alreadySelected = new Set(toIdArray(session?.selected_locations));
    return inventoryLocations.filter((location) => !isMainCountLocation(location) || alreadySelected.has(Number(location.id))
      || (!hasOtherLocations && location.is_receiving_destination !== false));
  }, [inventoryLocations, sessionId, session?.selected_locations]);

  const countLocationIds = session?.visible_location_ids ?? activeLocations;
  const sessionCountLocations = useMemo(() => {
    const locationMap = new Map();

    inventoryLocations.forEach((location) => {
      locationMap.set(Number(location.id), location);
    });

    lines.forEach((line) => {
      getLineLocationLines(line).forEach((locationLine) => {
        if (locationLine.location) {
          locationMap.set(Number(locationLine.location_id), locationLine.location);
        }
      });
    });

    return toIdArray(countLocationIds)
      .map((id) => locationMap.get(Number(id)))
      .filter(Boolean)
      .sort((a, b) => {
        const orderA = Number(a.sort_order || 0);
        const orderB = Number(b.sort_order || 0);
        if (orderA !== orderB) return orderA - orderB;
        if (Boolean(a.is_main) !== Boolean(b.is_main)) return a.is_main ? -1 : 1;
        return String(a.name || "").localeCompare(String(b.name || ""));
      });
  }, [countLocationIds, inventoryLocations, lines]);

  const sessionHasLocationCounts = sessionCountLocations.length > 0 &&
    lines.some((line) => getLineLocationLines(line).length > 0);

  const locationColumnLayout = useMemo(() => {
    const count = Math.max(sessionCountLocations.length, 1);

    if (count === 1) {
      return {
        columnWidth: "220px",
        windowWidth: "220px",
        stripMinWidth: "220px",
      };
    }

    if (count === 2) {
      return {
        columnWidth: "180px",
        windowWidth: "360px",
        stripMinWidth: "360px",
      };
    }

    return {
      columnWidth: "140px",
      windowWidth: "420px",
      stripMinWidth: `${count * 140}px`,
    };
  }, [sessionCountLocations.length]);

  useEffect(() => {
    if (!sessionCountLocations.length) {
      setActiveCountLocationId(null);
      return;
    }

    setActiveCountLocationId((current) => {
      const currentId = Number(current);
      const currentStillSelected = sessionCountLocations.some(
        (location) => Number(location.id) === currentId
      );

      return currentStillSelected ? currentId : Number(sessionCountLocations[0].id);
    });
  }, [sessionCountLocations]);

  const activeSessionLocation = useMemo(() => {
    const activeId = Number(activeCountLocationId);
    return (
      sessionCountLocations.find((location) => Number(location.id) === activeId) ||
      sessionCountLocations[0] ||
      null
    );
  }, [activeCountLocationId, sessionCountLocations]);

  const activeSessionLocationId = activeSessionLocation ? Number(activeSessionLocation.id) : null;

  const locationProgressById = useMemo(() => {
    const progress = new Map();

    sessionCountLocations.forEach((location) => {
      let total = 0;
      let counted = 0;

      lines.forEach((line) => {
        const locationLine = getLineLocationLines(line).find(
          (entry) => Number(entry.location_id) === Number(location.id)
        );

        if (!locationLine) return;

        total += 1;

        const factor = Number(line.__conversionFactor || 1) || 1;
        const expected = expectedInUnit(line, locationLine.expected_qty, factor, countLevel(line), locationLine.expected_exact_quantity);
        const key = getLocationCountKey(line.id, location.id);
        const value =
          liveLocationCounts[key] !== undefined
            ? liveLocationCounts[key]
            : locationLine.counted_qty ?? "";

        if ((!blindMode && expected <= 0) || !isBlankCountValue(value)) {
          counted += 1;
        }
      });

      progress.set(Number(location.id), { counted, total });
    });

    return progress;
  }, [blindMode, lines, liveLocationCounts, sessionCountLocations]);

  const getLocationCountValue = (line, locationId) => {
    const key = getLocationCountKey(line.id, locationId);
    if (liveLocationCounts[key] !== undefined) return liveLocationCounts[key];

    const locationLine = getLineLocationLines(line).find(
      (entry) => Number(entry.location_id) === Number(locationId)
    );

    return locationLine?.counted_qty ?? "";
  };

  const requiredFinalCountGaps = (() => {
    if (!canFinalizeSession) return [];

    const gaps = [];

    lines.forEach((line) => {
      const factor = Number(line.__conversionFactor || 1) || 1;
      const item = line.item || {};
      const itemLabel = item.code ? `${item.code} - ${item.name || "Item"}` : item.name || `Line ${line.id}`;
      const locationLines = getLineLocationLines(line);

      if (sessionHasLocationCounts && locationLines.length) {
        locationLines.forEach((locationLine) => {
          const expected = expectedInUnit(line, locationLine.expected_qty, factor, countLevel(line), locationLine.expected_exact_quantity);
          const value = getLocationCountValue(line, locationLine.location_id);

          if ((blindMode || expected > 0) && isBlankCountValue(value)) {
            gaps.push({
              item: itemLabel,
              location: locationLine.location?.name || "Location",
              expected,
            });
          }
        });
        return;
      }

      const expected = expectedInUnit(line, line.expected_qty, factor, countLevel(line), line.expected_exact_quantity);
      const value = liveCounted[line.id] !== undefined ? liveCounted[line.id] : line.counted_qty;

      if ((blindMode || expected > 0) && isBlankCountValue(value)) {
        gaps.push({
          item: itemLabel,
          location: null,
          expected,
        });
      }
    });

    return gaps;
  })();
  const missingRequiredFinalCounts = requiredFinalCountGaps.length;
  const hasMissingRequiredFinalCounts = missingRequiredFinalCounts > 0;

  const getLineLocationSummary = (line) => {
    const factor = line.__conversionFactor || 1;
    const locationLines = getLineLocationLines(line);
    let selectedExpected = 0;
    let finalQty = 0;
    const basis = countBasis(line);
    let exactTotals = Boolean(basis);
    let expectedSteps = 0n, finalSteps = 0n;
    let allCounted = Boolean(locationLines.length);

    locationLines.forEach((locationLine) => {
      const value = getLocationCountValue(line, locationLine.location_id);
      const locationExpected = expectedInUnit(line, locationLine.expected_qty, factor, countLevel(line), locationLine.expected_exact_quantity);
      selectedExpected += locationExpected;
      if (exactTotals) {
        const expected = locationLine.expected_exact_quantity || historicalExact(locationLine.expected_qty, basis);
        const counted = isBlankCountValue(value) ? expected : exactCountValue(line, locationLine, value);
        if (expected && counted) {
          expectedSteps += BigInt(expected.steps);
          finalSteps += BigInt(counted.steps);
        } else {
          exactTotals = false;
        }
      }

      if (isBlankCountValue(value)) {
        if (blindMode || locationExpected > 0) {
          allCounted = false;
        }
        finalQty += locationExpected;
        return;
      }

      const counted = Number(value);
      finalQty += counted;
    });

    if (exactTotals) {
      const projected = (steps) => Number(quantityFromExact({ ...basis, steps: String(steps) }, countLevel(line), 24));
      return { expected: projected(expectedSteps), finalQty: projected(finalSteps), difference: projected(finalSteps - expectedSteps), allCounted };
    }
    return { expected: selectedExpected, finalQty, difference: finalQty - selectedExpected, allCounted };
  };

  const sessionStats = (() => {
    const countedLines = lines.filter((line) => {
      if (sessionHasLocationCounts) {
        return getLineLocationSummary(line).allCounted;
      }

      const factor = line.__conversionFactor || 1;
      const expected = expectedInUnit(line, line.expected_qty, factor, countLevel(line), line.expected_exact_quantity);
      const value = liveCounted[line.id] !== undefined ? liveCounted[line.id] : line.counted_qty;
      return (!blindMode && expected <= 0) || !isBlankCountValue(value);
    }).length;

    const differenceLines = lines.filter((line) => {
      if (blindMode) return false;

      if (sessionHasLocationCounts) {
        const summary = getLineLocationSummary(line);
        return summary.difference !== null && summary.difference !== 0;
      }

      const factor = line.__conversionFactor || 1;
      const expected = expectedInUnit(line, line.expected_qty, factor, countLevel(line), line.expected_exact_quantity);
      const currentValue = liveCounted[line.id] !== undefined ? liveCounted[line.id] : line.counted_qty;

      if (currentValue === null || currentValue === "") {
        return false;
      }

      return countDifference(line, line, currentValue, expected) !== 0;
    }).length;

    return {
      totalLines: lines.length,
      countedLines,
      differenceLines,
      blindMode: blindMode ? "Blind" : "Visible",
    };
  })();

  const buildProductGroup = buildInventoryCountHierarchy;

  const normalizedCountSearch = normalizeInventoryCountSearchText(countSearch);
  const searchedLines = useMemo(() => {
    if (!normalizedCountSearch) return lines;

    return lines.filter((line) => {
      const item = line.item || {};
      const locationText = getLineLocationLines(line)
        .map((locationLine) => locationLine.location?.name || locationLine.location?.code || "")
        .join(" ");
      const searchableText = [
        item.code,
        item.name,
        getLineUnitLabel(line),
        item.unit?.name,
        item.subunit?.name,
        item.subunit2?.name,
        item.category?.subfamily?.family?.name,
        item.category?.subfamily?.name,
        item.category?.name,
        item.subcategory?.name,
        locationText,
      ].join(" ");

      return normalizeInventoryCountSearchText(searchableText).includes(normalizedCountSearch);
    });
  }, [lines, normalizedCountSearch]);

  // Capture progress describes actual inputs, not inferred zero-stock completion.
  const captureByLine = new Map(lines.map((line) => {
    const values = sessionHasLocationCounts
      ? getLineLocationLines(line).map((entry) => getLocationCountValue(line, entry.location_id))
      : [liveCounted[line.id] !== undefined ? liveCounted[line.id] : line.counted_qty];
    const filled = values.filter((value) => !isBlankCountValue(value)).length;
    const difference = blindMode ? false : sessionHasLocationCounts
      ? getLineLocationSummary(line).difference !== 0
      : filled > 0 && countDifference(line, line, values[0], expectedInUnit(line, line.expected_qty, line.__conversionFactor || 1, countLevel(line), line.expected_exact_quantity)) !== 0;
    return [line.id, { total: values.length, filled, blanks: values.length - filled, difference }];
  }));
  const captureProgress = [...captureByLine.values()].reduce((total, entry) => ({
    total: total.total + entry.total, filled: total.filled + entry.filled, blanks: total.blanks + entry.blanks,
  }), { total: 0, filled: 0, blanks: 0 });
  const visibleLines = pilcoreAppearance ? searchedLines.filter((line) => {
    const progress = captureByLine.get(line.id);
    return countViewFilter === "blank" ? progress.blanks > 0 : countViewFilter === "differences" && !blindMode ? progress.difference : true;
  }) : searchedLines;

  const runCountSave = (lineId, operation, fieldKey = lineId) => {
    if (!pilcoreAppearance) return operation();
    const generation = countSaveGenerationRef.current;
    pendingCountSavesRef.current += 1;
    setCountSaveState("saving");
    // Serializing each line preserves the order of rapid edits and location payloads.
    const previous = countSaveQueuesRef.current.get(lineId) || Promise.resolve();
    const request = previous.then(() => {
      if (generation !== countSaveGenerationRef.current) return;
      return operation();
    }).then(() => {
      if (generation === countSaveGenerationRef.current) failedCountSavesRef.current.delete(fieldKey);
    }).catch(() => {
      if (generation === countSaveGenerationRef.current) failedCountSavesRef.current.add(fieldKey);
    }).finally(() => {
      if (generation !== countSaveGenerationRef.current) return;
      pendingCountSavesRef.current -= 1;
      if (countSaveQueuesRef.current.get(lineId) === request) countSaveQueuesRef.current.delete(lineId);
      setCountSaveState(pendingCountSavesRef.current ? "saving" : failedCountSavesRef.current.size ? "error" : "saved");
    });
    countSaveQueuesRef.current.set(lineId, request);
    return request;
  };

  const hasUnsavedInputs = snapshotDate !== String(session?.snapshot_date || "").slice(0, 10) || String(notes || "") !== String(session?.notes || "") || lines.some((line) => {
      if (getLineLocationLines(line).length) return getLineLocationLines(line).some((entry) => {
        const value = liveLocationCounts[getLocationCountKey(line.id, entry.location_id)];
        return value !== undefined && String(value ?? "") !== String(entry.counted_qty ?? "");
      });
      return liveCounted[line.id] !== undefined && String(liveCounted[line.id] ?? "") !== String(line.counted_qty ?? "");
    });
  const unsavedCount = isOpen && Boolean(sessionId) &&
    (hasUnsavedInputs || countSaveState === "saving" || countSaveState === "error");
  draftRef.current = { liveCounted, liveLocationCounts, notes, snapshotDate, hasPending: unsavedCount };
  useEffect(() => {
    if (!unsavedCount) return;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [unsavedCount]);
  // Structural changes reload the sheet. Never allow them to erase an unsaved cell.
  const entryLocked = closeSaveBusy || Boolean(workbookAction) || (pilcoreAppearance && (structureBusy || confirmBusy));
  const structuralChangesBlocked = refreshStatus === "loading" || entryLocked || (pilcoreAppearance && Boolean(sessionId) &&
    (countSaveState === "saving" || countSaveState === "error" || hasUnsavedInputs));
  const workbookBlocked = structuralChangesBlocked || hasUnsavedInputs || addingPastItemId || finalizationBusy;
  const structuralChangesBlockedRef = useRef(false);
  structuralChangesBlockedRef.current = structuralChangesBlocked;
  const cannotChangeStructure = () => structuralChangesBlockedRef.current || (pilcoreAppearance &&
    (pendingCountSavesRef.current > 0 || failedCountSavesRef.current.size > 0));
  const closeWorkspace = async () => {
    if (workbookAction || closeSavePendingRef.current) return;
    const generation = countSaveGenerationRef.current;
    if (pilcoreAppearance && (pendingCountSavesRef.current || confirmBusy || structureBusy || correctionBusy || loading)) return;
    if (pilcoreAppearance && (failedCountSavesRef.current.size || hasUnsavedInputs) &&
      !await requestConfirmation("There are unsaved quantities or notes. Closing now will discard those entries. Close anyway?")) return;
    if (pilcoreAppearance && (pendingCountSavesRef.current || generation !== countSaveGenerationRef.current)) return;
    onClose();
  };

  const refreshExpectedStock = async (reason = "manual", savedDate = null) => {
    if (!isCounting || (refreshingExpectedRef.current && reason !== "date") || !can("view", true)) return;
    const generation = countSaveGenerationRef.current;
    const revision = sessionLoadRevisionRef.current;
    const refreshToken = {};
    refreshingExpectedRef.current = refreshToken;
    setRefreshStatus("loading");
    const retainDrafts = () => {
      // Copy only entries made in this workspace. A revoked location or a
      // newly blind count must never leave the old stock sheet on screen.
      const draft = draftRef.current;
      const entries = [];
      for (const line of lines) {
        const item = line.item?.name || String(line.item_id);
        if (draft.liveCounted[line.id] !== undefined) entries.push([item, draft.liveCounted[line.id]]);
        for (const cell of getLineLocationLines(line)) {
          const value = draft.liveLocationCounts[getLocationCountKey(line.id, cell.location_id)];
          if (value !== undefined) entries.push([item, cell.location?.name || cell.location_id, value]);
        }
      }
      if (draft.snapshotDate !== String(session.snapshot_date || "").slice(0, 10)) entries.push([t("Snapshot Date"), draft.snapshotDate]);
      if (draft.notes !== (session.notes || "")) entries.push([t("Notes"), draft.notes]);
      setRefreshRecovery(entries.map((entry) => entry.join(" · ")).join("\n"));
      countSaveGenerationRef.current += 1;
      countSaveQueuesRef.current.clear();
      failedCountSavesRef.current.clear();
      pendingCountSavesRef.current = 0;
      setCountSaveState("idle");
      setConfirmBusy(false);
      setFinalizationBusy(false);
      setConfirmAction(null);
      setFinalizationReview(null);
      setRefreshStatus("idle");
    };
    try {
      const fresh = await fetchSession(sessionId);
      if (generation !== countSaveGenerationRef.current || revision !== sessionLoadRevisionRef.current || refreshingExpectedRef.current !== refreshToken) return;
      if (!can("view", true)) { retainDrafts(); return; }
      const open = ["open", "in_progress", "reverted"].includes(getInventoryCountStatusMeta(fresh.status).key);
      if (!open || !compatibleExpectedScope(savedDate ? { ...session, snapshot_date: savedDate } : session, lines, fresh)) {
        if (draftRef.current.hasPending || pendingCountSavesRef.current) retainDrafts();
        else {
          applySession(fresh, sessionId);
          setRefreshStatus(open ? "updated" : "idle");
        }
        return;
      }
      // Only expected stock comes from this read. Count entries and notes can
      // be edited or acknowledged while it is in flight.
      setLines((current) => mergeExpectedStock(current, fresh.lines));
      setSession(current => current ? { ...current, expected_date: fresh.expected_date || fresh.snapshot_date } : current);
      const newCells = fresh.lines.length > lines.length ||
        fresh.lines.reduce((sum, line) => sum + getLineLocationLines(line).length, 0) >
        lines.reduce((sum, line) => sum + getLineLocationLines(line).length, 0);
      if (reason === "newStock" || newCells) {
        setCountSearch("");
        setCountViewFilter("all");
        setRefreshStatus("newStock");
      } else {
        setRefreshStatus("updated");
        if (reason === "finalize") setShowRefreshedFinalizePrompt(true);
      }
    } catch (error) {
      if (generation !== countSaveGenerationRef.current || revision !== sessionLoadRevisionRef.current || refreshingExpectedRef.current !== refreshToken) return;
      if ([403, 404].includes(error.response?.status)) retainDrafts();
      else setRefreshStatus("error");
    } finally {
      if (generation === countSaveGenerationRef.current && refreshingExpectedRef.current === refreshToken) {
        refreshingExpectedRef.current = false;
        if (revision !== sessionLoadRevisionRef.current) setRefreshStatus("idle");
      }
    }
  };

  const locationFilterTags = useMemo(() => {
    return toIdArray(activeLocations)
      .map((id) => {
        const label = sessionCountLocations.find((entry) => Number(entry.id) === Number(id))?.name;
        return label ? { id, label } : null;
      })
      .filter(Boolean);
  }, [activeLocations, sessionCountLocations]);

  const visiblePastItems = useMemo(() => {
    const query = pastItemsSearch.trim().toLowerCase();

    return pastItems.filter((item) => {
      if (!query) return true;

      const searchBlob = [
        item.name,
        item.code,
        buildInventoryCountHierarchy(item),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchBlob.includes(query);
    });
  }, [pastItems, pastItemsSearch]);

  const buildStartPayload = () => ({
    snapshot_date: snapshotDate,
    blind_mode: blindMode,
    notes,
    selectedLocations: selectedLocations.filter((id) => startContext?.locations.some((location) => Number(location.id) === Number(id))),
    ...(Object.values(scopeFilters).some((ids) => ids.length) ? { selectedFilters: scopeFilters } : {}),
  });

  const applyStartPolicy = (receivedPolicy) => {
    if (!receivedPolicy || typeof receivedPolicy.can_start !== "boolean" || !Array.isArray(receivedPolicy.locations)
      || typeof receivedPolicy.has_non_main_locations !== "boolean") {
      throw new Error("Invalid count start preview");
    }
    const policy = normalizeCountStartContext(receivedPolicy);
    setStartContext(policy);
    setStartContextError(false);
    setInventoryLocations(policy.locations);
    setSelectedLocations((current) => {
      const available = new Set(policy.locations.map((location) => Number(location.id)));
      const retained = current.filter((id) => available.has(Number(id)));
      return retained.length ? retained : soleCountLocation(policy);
    });
    const catalog = new Map(items.map((item) => [Number(item.id), item]));
    setStartPolicy(policy.can_start ? null : { ...policy, main_stock_items: policy.main_stock_items?.map((row) => ({
      ...row, physical_basis: inventoryBasis(catalog.get(Number(row.item_id))),
    })) });
    return policy.can_start;
  };

  const recoverMainStartSelection = async (generation) => {
    setStartPolicy(null);
    const withoutMain = normalizeCountStartContext({ ...startContext, locations: startContext?.locations || [], has_non_main_locations: true });
    const available = new Set(withoutMain.locations.map((location) => Number(location.id)));
    setStartContext(withoutMain);
    setInventoryLocations(withoutMain.locations);
    setSelectedLocations((current) => current.filter((id) => available.has(Number(id))));
    await loadAll(() => generation === countSaveGenerationRef.current);
    if (generation === countSaveGenerationRef.current) showNotice("Main cannot be included in this count because other locations exist. Transfer its stock to another location if needed.");
  };

  const recheckStartPolicy = async () => {
    if (!can("create", true) || startRequestRef.current) return;
    startRequestRef.current = true;
    const generation = countSaveGenerationRef.current;
    setLoading(true);
    try {
      const { data } = await axios.post("/api/purchasing/inventory-counts/start-preview", buildStartPayload(), { ...startRequestConfig(), skipCrossTabSync: true });
      if (generation !== countSaveGenerationRef.current) return;
      applyStartPolicy(data);
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      setStartPolicy(null);
      if (error.response?.data?.code === "COUNT_MAIN_LOCATION_NOT_COUNTABLE") {
        await recoverMainStartSelection(generation);
      } else showNotice("The count start requirements could not be checked. No count has been created. Please try again.");
    } finally {
      if (generation === countSaveGenerationRef.current) {
        startRequestRef.current = false;
        setLoading(false);
      }
    }
  };

  const reloadStartContextAfterTransfer = async () => {
    if (!can("create", true) || startRequestRef.current) return;
    startRequestRef.current = true;
    const generation = countSaveGenerationRef.current;
    setStartPolicy(null);
    setLoading(true);
    try {
      await loadAll(() => generation === countSaveGenerationRef.current);
    } finally {
      if (generation === countSaveGenerationRef.current) {
        startRequestRef.current = false;
        setLoading(false);
      }
    }
  };

  const handleGenerate = async () => {
    if (!can("create", true) || startRequestRef.current || !startContext || startContextError) return;
    const generation = countSaveGenerationRef.current;
    if (!selectedLocations.length) {
      showNotice("Select at least one location.");
      return;
    }

    if (snapshotDate > today) {
      showNotice("Future dates are not allowed for inventory counts.");
      return;
    }

    startRequestRef.current = true;
    setLoading(true);

    let nextSessionId;
    let startSubmitted = false;
    try {
      const payload = buildStartPayload();
      const preview = await axios.post("/api/purchasing/inventory-counts/start-preview", payload, { ...startRequestConfig(), skipCrossTabSync: true });
      if (generation !== countSaveGenerationRef.current) return;
      if (!applyStartPolicy(preview.data)) {
        startRequestRef.current = false;
        setLoading(false);
        return;
      }
      const available = new Set(normalizeCountStartContext(preview.data).locations.map((location) => Number(location.id)));
      if (payload.selectedLocations.some((id) => !available.has(Number(id)))) {
        startRequestRef.current = false;
        setLoading(false);
        showNotice("Count locations changed. Review the selected locations before generating the count.");
        return;
      }
      startSubmitted = true;
      const res = await axios.post("/api/purchasing/inventory-counts/start", payload, startRequestConfig());
      nextSessionId = res.data.id;
      if (generation !== countSaveGenerationRef.current) return;
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      console.error("[INV-COUNT][CREATE][ERROR]", error);
      const policy = error.response?.data;
      if (policy?.code === "COUNT_MAIN_LOCATION_NOT_COUNTABLE") {
        await recoverMainStartSelection(generation);
        if (generation !== countSaveGenerationRef.current) return;
      } else if (policy?.can_start === false && Array.isArray(policy.locations)) applyStartPolicy(policy);
      else showNotice(startSubmitted
        ? "The inventory count could not be confirmed. Refresh the count list before trying again."
        : "The count start requirements could not be checked. No count has been created. Please try again.");
      startRequestRef.current = false;
      setLoading(false);
      return;
    }

    try {
      await loadSession(nextSessionId, generation);
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      console.error("[INV-COUNT][CREATE][LOAD ERROR]", error);
      await showNotice(`Inventory count #${nextSessionId} was created, but its details could not be loaded. It remains available in the inventory count list.`, true);
      startRequestRef.current = false;
      setLoading(false);
      onClose();
      return;
    }

    if (generation !== countSaveGenerationRef.current) return;
    startRequestRef.current = false;
    setLoading(false);
  };

  const patchCountLine = async (lineId, payload) => {
    const { data } = await axios.patch(`/api/purchasing/inventory-counts/${sessionId}/line/${lineId}`, payload, requestConfig("update"));
    if (data?.success !== true) throw new Error("Count save was not confirmed");
    if (payload.location_counts) {
      if (Number(data.line?.id) !== Number(lineId) || !Array.isArray(data.line?.location_lines)) {
        throw new Error("Location count save was not confirmed");
      }
      for (const sent of payload.location_counts) {
        const saved = data.line.location_lines.find((cell) =>
          Number(cell.id) === Number(sent.id) && Number(cell.location_id) === Number(sent.location_id));
        const actual = saved?.counted_qty;
        const matches = sent.counted_qty === null ? actual === null :
          (typeof actual === "number" || typeof actual === "string") &&
          !isBlankCountValue(actual) && Number(actual) === Number(sent.counted_qty);
        if (!matches || (sent.counted_exact_quantity && saved?.counted_exact_quantity &&
            (String(saved.counted_exact_quantity.steps) !== String(sent.counted_exact_quantity.steps) ||
             Number(saved.counted_exact_quantity.unit_id) !== Number(sent.counted_exact_quantity.unit_id)))) {
          throw new Error("Location quantity was not confirmed");
        }
      }
    }
    return data;
  };

  const saveCount = async (lineId, value) => {
    if (!canEditSession || !can("update", true)) return;
    const target = lines.find((entry) => entry.id === lineId);
    if (countBasis(target) && liveCounted[lineId] === undefined) return;
    if (pilcoreAppearance && !countSaveQueuesRef.current.has(lineId) && !failedCountSavesRef.current.has(lineId) &&
      String(value ?? "") === String(lines.find((line) => line.id === lineId)?.counted_qty ?? "")) return;
    const generation = countSaveGenerationRef.current;
    return runCountSave(lineId, async () => {
    try {
      const line = lines.find((entry) => entry.id === lineId);

      const data = await patchCountLine(lineId, {
        counted_qty: value === "" ? null : value,
        unit_selected: line?.__selectedUnit?.key || null,
      });
      if (pilcoreAppearance && generation !== countSaveGenerationRef.current) return;

      setLines((prev) =>
        prev.map((entry) =>
          entry.id === lineId
            ? {
                ...entry,
                counted_exact_quantity: data.line?.counted_exact_quantity,
                counted_qty: formatInventoryCountInput(data.line?.counted_qty ?? (value === "" ? null : value)),
                difference: data.line?.difference ?? entry.difference,
              }
            : entry
        )
      );

      setLiveCounted((prev) => {
        if (pilcoreAppearance && String(prev[lineId] ?? "") !== String(value ?? "")) return prev;
        const next = { ...prev };
        delete next[lineId];
        return next;
      });
    } catch (error) {
      console.error("[INV-COUNT][SAVE][ERROR]", error);
      if (pilcoreAppearance) throw error;
      showNotice("The quantity could not be saved.");
    }
    });
  };

  const saveSessionDate = async () => {
    if (!canEditSession || !can("update", true) || !sessionId) return;
    if (snapshotDate === String(session?.snapshot_date || "").slice(0, 10) &&
      !countSaveQueuesRef.current.has("date") && !failedCountSavesRef.current.has("date")) return;
    const date = snapshotDate;
    const generation = countSaveGenerationRef.current;
    return runCountSave("date", async () => {
      if (!date || date > today) throw new Error("Choose a valid inventory count date that is not in the future.");
      const { data } = await axios.patch(`/api/purchasing/inventory-counts/${sessionId}/date`, {
        snapshot_date: date,
      }, requestConfig("update"));
      if (data?.success !== true || data.snapshot_date !== date) throw new Error("Date save was not confirmed");
      if (generation !== countSaveGenerationRef.current) return;
      setSession((prev) => prev ? { ...prev, snapshot_date: date, expected_date: prev.expected_date || prev.snapshot_date } : prev);
      setFinalizationReview(null);
      await refreshExpectedStock("date", date);
    });
  };

  const saveSessionNotes = async () => {
    if (!canEditSession || !can("update", true) || !sessionId) return;

    const currentNotes = session?.notes || "";
    if (String(notes || "") === String(currentNotes || "") &&
      (!pilcoreAppearance || (!countSaveQueuesRef.current.has("notes") && !failedCountSavesRef.current.has("notes")))) return;

    const generation = countSaveGenerationRef.current;
    return runCountSave("notes", async () => { try {
      const { data } = await axios.patch(`/api/purchasing/inventory-counts/${sessionId}/notes`, {
        notes,
      }, requestConfig("update"));
      if (data?.success !== true || data.notes !== notes) throw new Error("Notes save was not confirmed");
      if (pilcoreAppearance && generation !== countSaveGenerationRef.current) return;
      setSession((prev) => (prev ? { ...prev, notes } : prev));
    } catch (error) {
      console.error("[INV-COUNT][SAVE NOTES][ERROR]", error);
      if (pilcoreAppearance) throw error;
      showNotice(error.response?.data?.error || "The notes could not be saved.");
    } });
  };

  const buildLocationCountPayload = (line, cells = getLineLocationLines(line)) => cells.map((cell) => {
    const value = liveLocationCounts[getLocationCountKey(line.id, cell.location_id)] ?? cell.counted_qty;
    return { id: cell.id, location_id: cell.location_id, counted_qty: isBlankCountValue(value) ? null : value };
  });

  const saveLocationCount = async (line, locationId, value) => {
    if (!canEditSession || !can("update", true)) return;
    const cell = getLineLocationLines(line).find((entry) => Number(entry.location_id) === Number(locationId));
    if (!cell) return;
    const key = getLocationCountKey(line.id, locationId);
    if (countBasis(line) && liveLocationCounts[key] === undefined) return;
    if (pilcoreAppearance && !failedCountSavesRef.current.has(key) &&
      liveLocationCounts[key] === undefined && String(value ?? "") === String(cell.counted_qty ?? "")) return;
    const generation = countSaveGenerationRef.current;
    return runCountSave(line.id, async () => {
      try {
        const data = await patchCountLine(line.id, {
          location_counts: [{ id: cell.id, location_id: cell.location_id, counted_qty: value === "" ? null : value }],
          unit_selected: line.__selectedUnit?.key || null,
        });
        if (pilcoreAppearance && generation !== countSaveGenerationRef.current) return;
        setLines((prev) => prev.map((entry) => entry.id === line.id ? {
          ...entry, counted_exact_quantity: data.line.counted_exact_quantity, counted_qty: formatInventoryCountInput(data.line.counted_qty), difference: data.line.difference,
          location_lines: getLineLocationLines(entry).map((previousCell) => {
            const saved = data.line.location_lines.find((location) => Number(location.id) === Number(previousCell.id));
            return saved ? { ...previousCell, counted_exact_quantity: saved.counted_exact_quantity, counted_qty: formatInventoryCountInput(saved.counted_qty) } : previousCell;
          }),
        } : entry));
        setLiveLocationCounts((prev) => {
          if (String(prev[key] ?? "") !== String(value ?? "")) return prev;
          const next = { ...prev };
          delete next[key];
          return next;
        });
      } catch (error) {
        console.error("[INV-COUNT][SAVE LOCATION][ERROR]", error);
        if (pilcoreAppearance) throw error;
        showNotice("The quantity could not be saved.");
      }
    }, key);
  };

  const retryFailedCountSaves = () => {
    const failed = failedCountSavesRef.current;
    if (failed.has("date")) saveSessionDate();
    if (failed.has("notes")) saveSessionNotes();
    lines.forEach((line) => {
      if (failed.has(line.id)) saveCount(line.id, liveCounted[line.id] ?? line.counted_qty ?? "");
      getLineLocationLines(line).forEach((cell) => {
        if (failed.has(getLocationCountKey(line.id, cell.location_id))) {
          saveLocationCount(line, cell.location_id, getLocationCountValue(line, cell.location_id));
        }
      });
    });
  };

  const saveAndCloseWorkspace = async () => {
    if (closeSavePendingRef.current || workbookAction || loading || structureBusy || confirmBusy ||
        finalizationBusy || correctionBusy || addingPastItemId || refreshStatus === "loading") return;
    if (!canEditSession) {
      if (hasUnsavedInputs || pendingCountSavesRef.current || failedCountSavesRef.current.size) {
        showNotice("Some entries were not saved. Your values are still in the fields.");
      } else onClose();
      return;
    }
    if (!can("update", true)) return;
    const generation = countSaveGenerationRef.current;
    closeSavePendingRef.current = true;
    setCloseSaveBusy(true);
    try {
      // Include saves started by blur before the click, and capture inputs that
      // have not blurred yet. Existing queues serialize edits to the same line.
      const saves = [...countSaveQueuesRef.current.values(), saveSessionDate(), saveSessionNotes()];
      lines.forEach((line) => {
        const cells = getLineLocationLines(line);
        if (cells.length) {
          cells.forEach((cell) => {
            const key = getLocationCountKey(line.id, cell.location_id);
            if (liveLocationCounts[key] !== undefined || failedCountSavesRef.current.has(key)) {
              saves.push(saveLocationCount(line, cell.location_id, liveLocationCounts[key] ?? cell.counted_qty ?? ""));
            }
          });
        } else if (liveCounted[line.id] !== undefined || failedCountSavesRef.current.has(line.id)) {
          saves.push(saveCount(line.id, liveCounted[line.id] ?? line.counted_qty ?? ""));
        }
      });
      await Promise.all(saves);
      if (generation !== countSaveGenerationRef.current) return;
      if (failedCountSavesRef.current.size || pendingCountSavesRef.current || !can("update", true)) {
        showNotice("Some entries were not saved. Your values are still in the fields.");
        return;
      }
      onClose();
    } catch {
      if (generation === countSaveGenerationRef.current) showNotice("Some entries were not saved. Your values are still in the fields.");
    } finally {
      if (generation === countSaveGenerationRef.current) {
        closeSavePendingRef.current = false;
        setCloseSaveBusy(false);
      }
    }
  };

  const getLineCorrectionInitialValue = (line) => {
    const locationLines = getLineLocationLines(line);

    if (locationLines.length) {
      return locationLines.reduce((acc, locationLine) => {
        acc[getLocationCountKey(line.id, locationLine.location_id)] = locationLine.counted_qty ?? "";
        return acc;
      }, {});
    }

    return {
      counted_qty: line.counted_qty ?? "",
    };
  };

  const openLineCorrection = (line) => {
    if (!canCorrectFinalizedLines || !can("correct", true)) return;
    const currentOption =
      buildCorrectionUnitOptions(line).find((option) => option.key === line.__selectedUnit?.key) ||
      buildCorrectionUnitOptions(line)[0] ||
      null;
    const persistedFactor = Number(line.__conversionFactor || line.count_unit_factor || 0);
    const currentUnit = currentOption
      ? {
          ...currentOption,
          factor:
            Number.isFinite(persistedFactor) && persistedFactor > 0
              ? persistedFactor
              : currentOption.factor,
        }
      : null;

    const cells = getLineLocationLines(line);
    setCorrectionExact(cells.length ? Object.fromEntries(cells.map((cell) =>
      [getLocationCountKey(line.id, cell.location_id), savedCountExact(line, cell)])) : { counted_qty: savedCountExact(line) });
    setCorrectionEditKey(null);
    setCorrectionLine(line);
    setCorrectionValues(getLineCorrectionInitialValue(line));
    setCorrectionUnit(currentUnit);
    setCorrectionReason("");
  };

  const selectCorrectionUnit = (option) => {
    const previousFactor = Number(
      correctionUnit?.factor || correctionLine?.__conversionFactor || 1
    ) || 1;
    const recordedFactor = Number(
      correctionLine?.__conversionFactor || correctionLine?.count_unit_factor || 0
    );
    const returnsToRecordedUnit =
      option?.key && option.key === correctionLine?.__selectedUnit?.key;
    const nextFactor =
      returnsToRecordedUnit && Number.isFinite(recordedFactor) && recordedFactor > 0
        ? recordedFactor
        : Number(option?.factor || 1) || 1;

    if (option?.key !== correctionUnit?.key) {
      setCorrectionValues((previous) =>
        Object.fromEntries(
          Object.entries(previous).map(([key, value]) => {
            if (isBlankCountValue(value)) return [key, value];
            const converted = (Number(value || 0) / previousFactor) * nextFactor;
            return [key, formatInventoryCountInput(quantityFromExact(correctionExact[key], option.key) ?? converted)];
          })
        )
      );
    }
    setCorrectionUnit(option ? { ...option, factor: nextFactor } : option);
  };

  const closeLineCorrection = () => {
    if (correctionBusy) return;
    setCorrectionLine(null);
    setCorrectionValues({});
    setCorrectionUnit(null);
    setCorrectionReason("");
  };

  const submitLineCorrection = async () => {
    if (!canCorrectFinalizedLines || !can("correct", true) || !correctionLine || !sessionId) return;
    const generation = countSaveGenerationRef.current;

    const reason = String(correctionReason || "").trim();
    if (!reason) {
      showNotice("Please enter a reason for this correction.");
      return;
    }

    const locationLines = getLineLocationLines(correctionLine);
    const payload = {
      reason,
      unit_selected: correctionUnit?.key || correctionLine.__selectedUnit?.key || null,
    };

    if (locationLines.length) {
      payload.location_counts = locationLines.map((locationLine) => {
        const key = getLocationCountKey(correctionLine.id, locationLine.location_id);
        return {
          id: locationLine.id,
          location_id: locationLine.location_id,
          counted_qty: correctionValues[key],
          ...(correctionExact[key] && { counted_exact_quantity: correctionExact[key] }),
        };
      });
    } else {
      payload.counted_qty = correctionValues.counted_qty;
      if (correctionExact.counted_qty) payload.counted_exact_quantity = correctionExact.counted_qty;
    }

    setCorrectionBusy(true);

    try {
      await axios.post(
        `/api/purchasing/inventory-counts/${sessionId}/line/${correctionLine.id}/correct`,
        payload, requestConfig("correct")
      );
      await loadSession(sessionId, generation);
      if (generation !== countSaveGenerationRef.current) return;
      setCorrectionLine(null);
      setCorrectionValues({});
      setCorrectionUnit(null);
      setCorrectionReason("");
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      const data = error.response?.data || {};
      console.error("[INV-COUNT][CORRECT LINE][ERROR]", error);
      showNotice(data.error || "The inventory count line could not be corrected.");
    } finally {
      if (generation === countSaveGenerationRef.current) setCorrectionBusy(false);
    }
  };

  const flushPendingCounts = async () => {
    if (!canEditSession || !can("update", true)) return;
    if (pilcoreAppearance) {
      await Promise.all([...countSaveQueuesRef.current.values()]);
      if (failedCountSavesRef.current.size) throw new Error(t("Some entries were not saved. Your values are still in the fields."));
    }
    const lineById = new Map(lines.map((line) => [Number(line.id), line]));
    const requests = [];

    Object.entries(liveCounted).forEach(([lineId, value]) => {
      const line = lineById.get(Number(lineId));
      if (!line || getLineLocationLines(line).length) return;
      const persistedValue = line.counted_qty ?? "";
      if (String(value ?? "") === String(persistedValue ?? "")) return;

      requests.push(
        patchCountLine(lineId, {
          counted_qty: value === "" ? null : value,
          unit_selected: line?.__selectedUnit?.key || null,
        })
      );
    });

    lines.forEach((line) => {
      if (!getLineLocationLines(line).length) return;

      const pendingCells = getLineLocationLines(line).filter((locationLine) => {
        const key = getLocationCountKey(line.id, locationLine.location_id);
        if (liveLocationCounts[key] === undefined) return false;
        const persistedValue = locationLine.counted_qty ?? "";
        return String(liveLocationCounts[key] ?? "") !== String(persistedValue ?? "");
      });

      if (!pendingCells.length) return;

      requests.push(
        patchCountLine(line.id, {
          location_counts: buildLocationCountPayload(line, pendingCells),
          unit_selected: line?.__selectedUnit?.key || null,
        })
      );
    });

    if (requests.length) {
      await Promise.all(requests);
    }
  };

  const reviewNewStock = async () => {
    setFinalizationReview(null);
    setConfirmAction(null);
    await refreshExpectedStock("newStock");
  };

  const fetchFinalizationReview = async (payload, generation, previousReview) => {
    let response;
    try {
      response = await axios.post(`/api/purchasing/inventory-counts/${sessionId}/finalize-preview`, payload, requestConfig("finalize"));
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return null;
      if (error.response?.data?.code !== "INVENTORY_COUNT_NEW_STOCK") throw error;
      await reviewNewStock();
      return null;
    }
    if (generation !== countSaveGenerationRef.current) return null;
    const preview = response.data;
    if (!preview?.fingerprint || !Array.isArray(preview.rows)) throw new Error(t("The final stock review could not be loaded."));
    const visibleCells = new Map(lines.map((line) => [Number(line.item_id),
      new Set(getLineLocationLines(line).map((cell) => Number(cell.location_id)))]));
    if (preview.rows.some((row) => {
      const cells = visibleCells.get(Number(row.item_id));
      return !cells || row.expected_location_ids?.some((id) => !cells.has(Number(id)));
    })) {
      await reviewNewStock();
      return null;
    }
    return markReviewedStock(preview, lines, previousReview);
  };

  const failedFinalizationReview = (error, payload, preview) => {
    const data = error.response?.data || {};
    const locationConflict = ["LOCATION_LEDGER_DISCONTINUITY", "LOCATION_LEDGER_EVENT_CONFLICT"].includes(data.code);
    let message = data.error || error.message || "The final stock review could not be loaded.";
    if (locationConflict) {
      message = t(data.code === "LOCATION_LEDGER_DISCONTINUITY"
        ? "The saved stock does not match the location movement history."
        : "The location movement could not be recorded because its saved data conflicts with this operation.");
      if (!data.details?.issues?.length) message += " " + t("The server did not include the affected item, location or quantities in its response.");
    }
    return { payload, error: t(message), errorDetails: data.details || null,
      preview: { ...(preview || { effective_date: snapshotDate, rows: [], issues: [] }), can_finalize: false, fingerprint: null } };
  };

  const handleConfirmAction = async (_choiceValue, checkboxChecked = false) => {
    if (!confirmAction || !can(confirmAction.type, true)) return;
    if (pilcoreAppearance && pendingCountSavesRef.current) return;
    const generation = countSaveGenerationRef.current;

    setConfirmBusy(true);
    let finalizePayload;

    try {
      if (confirmAction.type === "finalize") {
        const allowBlankExpectedCounts = Boolean(
          confirmAction.allowBlankExpectedCounts ||
            (confirmAction.checkbox?.name === "allow_blank_expected_counts" && checkboxChecked)
        );

        if (confirmAction.checkbox?.required && !allowBlankExpectedCounts) {
          setConfirmBusy(false);
          return;
        }

        if (pilcoreAppearance && String(notes || "") !== String(session?.notes || "")) {
          await saveSessionNotes();
          if (failedCountSavesRef.current.has("notes")) throw new Error(t("The notes could not be saved."));
        }
        await saveSessionDate();
        await flushPendingCounts();
        if (generation !== countSaveGenerationRef.current) return;
        finalizePayload = {
          allow_blank_expected_counts: allowBlankExpectedCounts,
          finalize_date_mode: "snapshot",
        };

        const preview = await fetchFinalizationReview(finalizePayload, generation);
        if (preview) setFinalizationReview({ preview, payload: finalizePayload, error: "" });
      }

      if (confirmAction.type === "delete") {
        await axios.delete(`/api/purchasing/inventory-counts/${sessionId}`, requestConfig("delete"));
        if (generation === countSaveGenerationRef.current) onClose();
      }
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      console.error("[INV-COUNT][SESSION ACTION][ERROR]", error);
      if (finalizePayload) setFinalizationReview(failedFinalizationReview(error, finalizePayload));
      else showNotice(error.response?.data?.error || error.message || "The session action could not be completed.");
    } finally {
      if (generation === countSaveGenerationRef.current) {
        setConfirmBusy(false);
        setConfirmAction(null);
      }
    }
  };

  const refreshFinalizationReview = async (resolutions) => {
    if (!canFinalizeSession || !can("finalize", true) || !finalizationReview || finalizationPendingRef.current) return;
    const generation = countSaveGenerationRef.current;
    const payload = { ...finalizationReview.payload, main_resolutions: resolutions };
    finalizationPendingRef.current = true;
    setFinalizationBusy(true);
    try {
      const preview = await fetchFinalizationReview(payload, generation, finalizationReview.preview);
      if (preview) setFinalizationReview({ preview, payload, error: "" });
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      setFinalizationReview((current) => current && failedFinalizationReview(error, payload, current.preview));
    } finally {
      if (generation === countSaveGenerationRef.current) { finalizationPendingRef.current = false; setFinalizationBusy(false); }
    }
  };

  const finalizeReviewedCount = async (resolutions) => {
    if (!canFinalizeSession || !can("finalize", true) || !finalizationReview?.preview?.can_finalize || !finalizationReview.preview.fingerprint || finalizationPendingRef.current) return;
    const generation = countSaveGenerationRef.current;
    const payload = { ...finalizationReview.payload, main_resolutions: resolutions };
    finalizationPendingRef.current = true;
    setFinalizationBusy(true);
    try {
      await axios.post(`/api/purchasing/inventory-counts/${sessionId}/finalize`, {
        ...payload, preview_fingerprint: finalizationReview.preview.fingerprint,
      }, requestConfig("finalize"));
      if (generation === countSaveGenerationRef.current) onClose();
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      const needsLocationReview = ["LOCATION_LEDGER_DISCONTINUITY", "LOCATION_LEDGER_EVENT_CONFLICT"].includes(error.response?.data?.code);
      if (error.response?.data?.code === "INVENTORY_COUNT_NEW_STOCK") {
        await reviewNewStock();
      } else if (needsLocationReview) {
        setFinalizationReview((current) => current && failedFinalizationReview(error, payload, current.preview));
      } else if (error.response?.data?.code === "INVENTORY_COUNT_PREVIEW_STALE") {
        try {
          const preview = await fetchFinalizationReview(payload, generation, finalizationReview.preview);
          if (preview) setFinalizationReview({ preview, payload, error: t("Stock or receipts changed. Review the updated quantities and confirm again.") });
        } catch (refreshError) {
          if (generation === countSaveGenerationRef.current) setFinalizationReview((current) => current && failedFinalizationReview(refreshError, payload, current.preview));
        }
      } else {
        setFinalizationReview((current) => current && failedFinalizationReview(error, payload, current.preview));
      }
    } finally {
      if (generation === countSaveGenerationRef.current) { finalizationPendingRef.current = false; setFinalizationBusy(false); }
    }
  };

  const returnFromFinalReview = () => {
    if (finalizationBusy) return;
    setFinalizationReview(null);
    refreshExpectedStock();
  };

  const promptDeleteSession = () => {
    if (!canDeleteSession || !can("delete", true)) return;
    setConfirmAction({
      type: "delete",
      title: "Delete Inventory Count",
      message: "This will permanently remove the live count session and all of its captured lines.",
      confirmLabel: "Delete Count",
      tone: "danger",
    });
  };

  const promptFinalizeSession = useCallback(() => {
    if (!canFinalizeSession || !can("finalize", true)) return;
    const isRevertedSession = sessionStatusKey === "reverted";
    const missingCellsMessage = `${t("Blank cells with expected stock")} (${missingRequiredFinalCounts}). ${t("Fill them or confirm that they should stay uncounted.")}`;
    const dateMessage = `${t("Inventory posting date")}: ${formatInventoryCountDate(snapshotDate)}. ${t("The count will be posted on this date. Later movements will be preserved.")}`;

    setConfirmAction({
      type: "finalize",
      stockNotice: lines.some((line) => line.expected_changed),
      title: hasMissingRequiredFinalCounts
        ? "Missing Count Cells"
        : isRevertedSession
        ? "Finalize Reverted Count"
        : "Finalize Inventory Count",
      message: hasMissingRequiredFinalCounts
        ? blindMode ? "Some cells are blank. Fill them or confirm that they should stay uncounted." : `${missingCellsMessage} ${dateMessage}`
        : dateMessage,
      checkbox: hasMissingRequiredFinalCounts
        ? {
            name: "allow_blank_expected_counts",
            label: "Allow blank counted cells",
            helper: "Blank cells stay uncounted.",
            required: true,
          }
        : null,
      confirmLabel: hasMissingRequiredFinalCounts ? "Review with blank cells" : "Review Count",
      tone: "warning",
    });
  }, [
    can,
    canFinalizeSession,
    lines,
    hasMissingRequiredFinalCounts,
    missingRequiredFinalCounts,
    sessionStatusKey,
    snapshotDate,
    blindMode,
    t,
  ]);

  useEffect(() => {
    if (!showRefreshedFinalizePrompt) return;
    setShowRefreshedFinalizePrompt(false);
    promptFinalizeSession();
  }, [showRefreshedFinalizePrompt, promptFinalizeSession]);

  useEffect(() => {
    if (!autoPromptFinalize || !contentReady || loading || !sessionId || confirmAction) return;
    if (autoPromptFinalizeSessionRef.current === sessionId) return;

    autoPromptFinalizeSessionRef.current = sessionId;
    promptFinalizeSession();
  }, [autoPromptFinalize, confirmAction, contentReady, loading, promptFinalizeSession, sessionId]);

  const referenceTitle = (row, line) => !blindMode && row?.reference_expected_qty != null && row.expected_changed
    ? `${t("Saved expected stock")} · ${countNumber(line, expectedInUnit(line, row.reference_expected_qty, line.__conversionFactor || 1))}` : undefined;

  const renderChangedStock = (line) => isCounting && line.expected_changed
    ? <small className="ic-expectedChanged">{t("Stock updated")}</small> : null;

  const renderExpectedRefresh = () => <>
    <span className="ic-refreshStatus">{t("Expected at count date")}: {formatInventoryCountDate(session?.expected_date || session?.snapshot_date || snapshotDate)}</span>
    {isCounting && <button type="button" className="ic-btn ic-btn--ghost" disabled={closeSaveBusy || refreshStatus === "loading" || structureBusy || Boolean(workbookAction)}
      onClick={() => refreshExpectedStock()}><FiRefreshCw aria-hidden="true" /> {t("Refresh expected stock")}</button>}
    {["updated", "error", "newStock"].includes(refreshStatus) && <small className="ic-muted" role={refreshStatus === "updated" ? "status" : "alert"}>
      {t(refreshStatus === "newStock" ? "New stock arrived in the counted locations. Review the new lines before finalizing." :
        refreshStatus === "updated" ? "Expected stock updated." : "Expected stock could not be refreshed. Try again.")}
    </small>}
  </>;

  const renderSessionActions = () => <>
    {isCounting && <input ref={workbookFileRef} hidden type="file" accept=".xlsx" aria-label={t("Count workbook")}
      disabled={!canEditSession || Boolean(workbookBlocked)} onChange={(event) => {
        const file = event.target.files?.[0]; event.target.value = "";
        if (file) handleCountWorkbook(file);
      }} />}
    <InventoryCountActions label={t("Actions")} disabled={closeSaveBusy} groups={[
      { label: t("Count workbook"), actions: [
        ...(isCounting ? [
          { label: t(workbookAction === "download" ? "Downloading..." : "Download count template"), icon: FiDownload,
            disabled: Boolean(workbookBlocked), onClick: () => handleCountWorkbook() },
          { label: t(workbookAction === "import" ? "Importing..." : "Upload completed count"), icon: FiUpload,
            disabled: !canEditSession || Boolean(workbookBlocked), onClick: () => workbookFileRef.current?.click() },
        ] : []),
        { label: t("Export Excel"), disabled: Boolean(workbookAction) || (pilcoreAppearance && countSaveState === "saving"), onClick: handleExportExcel },
      ] },
      { label: t("Inventory Count"), actions: [
        ...(canFinalizeSession ? [{ label: t("Finalize"),
          disabled: refreshStatus === "loading" || Boolean(workbookAction) || (pilcoreAppearance && (countSaveState === "saving" || countSaveState === "error" || structureBusy)),
          onClick: () => { if (!pilcoreAppearance || (!pendingCountSavesRef.current && !structureBusy)) refreshExpectedStock("finalize"); } }] : []),
        { label: t("Close"), disabled: Boolean(workbookAction) || (pilcoreAppearance && countSaveState === "saving"), onClick: closeWorkspace },
      ] },
      { label: t("Delete"), danger: true, actions: canDeleteSession ? [{ label: t("Delete"),
        disabled: Boolean(workbookAction) || (pilcoreAppearance && countSaveState === "saving"),
        onClick: () => { if (!pilcoreAppearance || !pendingCountSavesRef.current) promptDeleteSession(); } }] : [] },
    ]} />
  </>;

  useEffect(() => {
    if (!showPastItemsPopup || !sessionId || !can("update", true)) return;

    let cancelled = false;

    const loadPastItems = async () => {
      try {
        const res = await axios.get(
          `/api/purchasing/inventory/historical-items?business_unit_id=${businessUnitId}`, requestConfig("update")
        );

        const activeItemIds = new Set(
          lines
            .map((line) => Number(line.item_id || line.item?.id || 0))
            .filter(Boolean)
        );

        const excludedItemIds = new Set(toIdArray(session?.excluded_item_ids));
        const removedCandidates = (session?.excluded_items || [])
          .filter((item) => item?.id && !activeItemIds.has(Number(item.id)))
          .map((item) => ({
            ...item,
            __removedFromCount: true,
          }));

        const filtered = [...items, ...(res.data || [])]
          .filter((item) => !isDirectConsumptionItem(item) && !activeItemIds.has(Number(item.id)))
          .map((item) => ({ ...item, __removedFromCount: excludedItemIds.has(Number(item.id)) }));

        if (!cancelled && can("update", true)) {
          const byId = new Map();
          removedCandidates.forEach((item) => {
            byId.set(Number(item.id), item);
          });
          filtered.forEach((item) => {
            if (!byId.has(Number(item.id))) {
              byId.set(Number(item.id), item);
            }
          });
          setPastItems(Array.from(byId.values()));
        }
      } catch (error) {
        console.error("[INV-COUNT][PAST ITEMS][ERROR]", error);
      }
    };

    loadPastItems();

    return () => {
      cancelled = true;
    };
  }, [
    businessUnitId,
    lines,
    session?.excluded_item_ids,
    session?.excluded_items,
    sessionId,
    showPastItemsPopup,
    items,
    can,
    requestConfig,
  ]);

  const handleAddPastItem = async (itemId) => {
    if (!canEditSession || !can("update", true) || cannotChangeStructure()) return;
    const generation = countSaveGenerationRef.current;
    try {
      setAddingPastItemId(itemId);
      if (pilcoreAppearance) setStructureBusy(true);

      await axios.post(`/api/purchasing/inventory-counts/${sessionId}/add-item`, {
        item_id: itemId,
      }, requestConfig("update"));

      if (generation !== countSaveGenerationRef.current) return;
      await loadSession(sessionId, generation);
      setShowPastItemsPopup(false);
    } catch (error) {
      console.error("[INV-COUNT][ADD PAST ITEM][ERROR]", error);
      showNotice("The item could not be added to the count.");
    } finally {
      if (generation === countSaveGenerationRef.current) {
        setAddingPastItemId(null);
        if (pilcoreAppearance) setStructureBusy(false);
      }
    }
  };

  const handleSelectCountUnit = async (line, option) => {
    if (!canEditSession || !can("update", true) || cannotChangeStructure()) return;
    const generation = countSaveGenerationRef.current;
    if (pilcoreAppearance) setStructureBusy(true);

    const lineId = line.id;
    const persistedCount =
      liveCounted[lineId] !== undefined ? liveCounted[lineId] : line.counted_qty ?? null;

    try {
      const payload = getLineLocationLines(line).length
        ? {
            location_counts: buildLocationCountPayload(line),
            unit_selected: option.key,
          }
        : {
            counted_qty: persistedCount === "" ? null : persistedCount,
            unit_selected: option.key,
          };

      const basis = countBasis(line);
      if (basis) {
        const convert = (entry, cell) => {
          if (isBlankCountValue(entry.counted_qty)) return entry;
          const exact = savedCountExact(line, cell);
          return exact
            ? { ...entry, counted_exact_quantity: exact, counted_qty: quantityFromExact(exact, option.key) }
            : { ...entry, counted_qty: Number(entry.counted_qty) / Number(line.__conversionFactor || line.count_unit_factor || 1) * option.factor };
        };
        if (payload.location_counts) payload.location_counts = payload.location_counts.map((entry) =>
          convert(entry, getLineLocationLines(line).find((cell) => Number(cell.id) === Number(entry.id))));
        else Object.assign(payload, convert(payload, line));
      }
      const saved = await patchCountLine(lineId, payload);
      if (generation !== countSaveGenerationRef.current) return;
      if (saved.line?.count_unit_used && saved.line.count_unit_used !== option.key) {
        throw new Error("Count unit save was not confirmed");
      }
      setLines((previous) => previous.map((entry) => entry.id !== lineId ? entry
        : mergeCountUnitResponse(entry, saved.line, payload, option)));
      setShowUnitModal(false);
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      console.error("[INV-COUNT][SELECT UNIT][ERROR]", error);
      try {
        await loadSession(sessionId, generation);
      } catch (reloadError) {
        console.error("[INV-COUNT][SELECT UNIT][RELOAD ERROR]", reloadError);
      }
      showNotice("The count unit could not be saved.");
    } finally {
      if (pilcoreAppearance && generation === countSaveGenerationRef.current) setStructureBusy(false);
    }
  };

  const handleDeleteLine = async (line) => {
    if (!canEditSession || !can("update", true) || !line?.id || cannotChangeStructure()) return;
    const generation = countSaveGenerationRef.current;

    const itemLabel = line.item?.code
      ? `${line.item.code} - ${line.item.name || "Item"}`
      : line.item?.name || "this item";

    if (!(await requestConfirmation(`Remove ${itemLabel} from this count? Any captured quantities for this item will be ignored.`))) {
      return;
    }
    if (generation !== countSaveGenerationRef.current || cannotChangeStructure()) return;

    try {
      if (pilcoreAppearance) setStructureBusy(true);
      await axios.delete(`/api/purchasing/inventory-counts/${sessionId}/line/${line.id}`, requestConfig("update"));
      await loadSession(sessionId, generation);
    } catch (error) {
      console.error("[INV-COUNT][DELETE LINE][ERROR]", error);
      showNotice(error.response?.data?.error || "The item could not be removed from the count.");
    } finally {
      if (pilcoreAppearance && generation === countSaveGenerationRef.current) setStructureBusy(false);
    }
  };

  const openLocationsEditor = () => {
    if (cannotChangeStructure()) return;
    locationsEditSnapshotRef.current = selectedLocations;
    setLocationSearch("");
    setLocationsEditOpen(true);

  };

  const cancelLocationsEditor = () => {
    const snapshot = locationsEditSnapshotRef.current;
    if (snapshot) {
      setSelectedLocations(snapshot);
    }

    locationsEditSnapshotRef.current = null;
    setLocationsEditOpen(false);
  };

  const applyLocations = async (
    rawLocationIds = selectedLocations,
    { closeEditor = true, requireConfirmation = true } = {}
  ) => {
    if (cannotChangeStructure()) return;
    const generation = countSaveGenerationRef.current;
    const available = new Set([...selectableLocations.map((location) => Number(location.id)), ...toIdArray(session?.selected_locations)]);
    const nextLocationIds = Array.from(new Set(toIdArray(rawLocationIds))).filter((id) => available.has(id));

    if (!nextLocationIds.length) {
      showNotice("Select at least one location.");
      return;
    }

    if (!sessionId) {
      setSelectedLocations(nextLocationIds);
      locationsEditSnapshotRef.current = null;
      if (closeEditor) setLocationsEditOpen(false);
      return;
    }

    if (!canEditSession || !can("update", true)) return;

    const previousLocationIds = toIdArray(session?.selected_locations);
    const addedIds = nextLocationIds.filter((id) => !previousLocationIds.includes(id));
    const removedIds = previousLocationIds.filter((id) => !nextLocationIds.includes(id));
    if (!addedIds.length && !removedIds.length) {
      locationsEditSnapshotRef.current = null;
      if (closeEditor) setLocationsEditOpen(false);
      return;
    }

    if (
      removedIds.length > 0 && requireConfirmation &&
      !(await requestConfirmation("Apply these locations? Removed location columns will be deleted from this count and their captured quantities will be ignored."))
    ) {
      return;
    }
    if (generation !== countSaveGenerationRef.current || cannotChangeStructure()) return;

    try {
      setStructureBusy(true);
      await flushPendingCounts();
      if (generation !== countSaveGenerationRef.current) return;
      const saveLocations = (confirmDiscard) => axios.patch(`/api/purchasing/inventory-counts/${sessionId}/locations`, {
        selected_locations: nextLocationIds,
        ...(confirmDiscard ? { confirm_discard: true } : {}),
      }, requestConfig("update"));
      try {
        await saveLocations(removedIds.length > 0 && requireConfirmation);
      } catch (error) {
        // Older counts can carry a quantity without a location cell. The
        // server also requires explicit confirmation before converting them.
        if (error.response?.data?.code !== "COUNT_LOCATION_QUANTITIES_CONFIRMATION_REQUIRED") throw error;
        if (!await requestConfirmation(error.response.data.error) || generation !== countSaveGenerationRef.current) return;
        await saveLocations(true);
      }
      const updatedSession = await loadSession(sessionId, generation);
      if (!updatedSession || generation !== countSaveGenerationRef.current) return;
      locationsEditSnapshotRef.current = null;
      if (closeEditor) setLocationsEditOpen(false);
      if (addedIds.length) {
        setAddedLocationIds((previous) => Array.from(new Set([...previous, ...addedIds])));
        setActiveCountLocationId(addedIds[0]);
        setLocationFeedback({
          message: addedIds.length === 1 ? "Location added." : "Locations added.",
          names: addedIds.map((id) => inventoryLocations.find((location) => Number(location.id) === id)?.name).filter(Boolean).join(", "),
        });
      }
    } catch (error) {
      if (generation !== countSaveGenerationRef.current) return;
      console.error("[INV-COUNT][APPLY LOCATIONS][ERROR]", error);
      showNotice(error.response?.data?.error || "The count locations could not be updated.");
    } finally {
      if (generation === countSaveGenerationRef.current) setStructureBusy(false);
    }
  };

  const handleApplyLocations = async () => {
    await applyLocations(selectedLocations);
  };

  const handleRemoveLocationChip = async (id) => {
    const nextLocationIds = selectedLocations.filter((value) => Number(value) !== Number(id));

    if (!nextLocationIds.length) {
      showNotice("At least one location must remain selected.");
      return;
    }

    if (!sessionId) {
      setSelectedLocations(nextLocationIds);
      return;
    }

    await applyLocations(nextLocationIds, { closeEditor: false });
  };

  const refreshImportedSession = async (generation) => {
    try {
      if (!await loadSession(sessionId, generation)) return false;
      setSessionLoadError("");
      return true;
    } catch {
      if (generation === countSaveGenerationRef.current) {
        setSessionLoadError("The workbook was imported. Reload the count to see the saved quantities.");
      }
      return false;
    }
  };

  const handleCountWorkbook = async (file) => {
    const permission = file ? "update" : "view";
    if (!isCounting || workbookBlocked || pendingCountSavesRef.current || !can(permission, true)) return;
    if (file && !isCountWorkbookFile(file)) {
      showNotice("Choose an .xlsx count template of 10 MB or less.");
      return;
    }
    const generation = countSaveGenerationRef.current;
    const stillOpen = () => generation === countSaveGenerationRef.current && can(permission, true);
    const baseUrl = `/api/purchasing/inventory-counts/${sessionId}`;
    setWorkbookAction(file ? "import" : "download");
    try {
      const config = { ...requestConfig(permission), params: { language: uiLanguage } };
      if (file) {
        const data = await uploadCountWorkbook(file, config, sessionId);
        if (!stillOpen() || !await refreshImportedSession(generation)) return;
        showNotice(data.imported_cells === 0 ? "No quantities changed." : `Quantities updated: ${data.imported_cells}.`);
      } else {
        const { data } = await axios.get(`${baseUrl}/template`, { ...config, responseType: "blob" });
        if (!stillOpen()) return;
        const link = document.createElement("a");
        const url = URL.createObjectURL(data);
        link.href = url;
        link.download = `inventory-count-${sessionId}-template.xlsx`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (error) {
      let detail = error.response?.data;
      if (detail instanceof Blob) {
        try { detail = JSON.parse(await detail.text()); } catch { detail = null; }
      }
      if (stillOpen()) showNotice(detail?.error || (file ? "The import could not be confirmed. Reopen the count before trying again." : "The count template could not be downloaded."));
    } finally {
      if (generation === countSaveGenerationRef.current) setWorkbookAction(null);
    }
  };

  const handleExportExcel = () => {
    const metadataRows = [
      ["Inventory Count", sessionId ? `#${sessionId}` : "Draft"],
      ["Status", sessionStatusMeta.label || ""],
      ["Snapshot Date", formatInventoryCountDate(snapshotDate)],
      [pilcoreAppearance ? "Entered cells" : "Counted Lines", pilcoreAppearance ? `${captureProgress.filled} / ${captureProgress.total}` : `${sessionStats.countedLines} / ${sessionStats.totalLines}`],
      ["Differences", blindMode ? "Hidden in blind mode" : sessionStats.differenceLines],
      ["Visibility", sessionStats.blindMode],
      ["Notes", notes || ""],
      ["Exported At", new Date().toLocaleString()],
      [],
      ["Totals", ""],
      ["Total Lines", lines.length],
      [pilcoreAppearance ? "Entered cells" : "Counted Lines", pilcoreAppearance ? captureProgress.filled : sessionStats.countedLines],
      [pilcoreAppearance ? "Blank cells" : "Missing Count Lines", pilcoreAppearance ? captureProgress.blanks : Math.max(0, sessionStats.totalLines - sessionStats.countedLines)],
    ];

    const countHeaders = [
      "Product Group",
      "Code",
      "Item",
      "Unit",
      "Expected",
    ];

    if (sessionHasLocationCounts) {
      sessionCountLocations.forEach((location) => {
        countHeaders.push(`${location.name} Expected`, `${location.name} Counted`, `${location.name} Difference`, `${location.name} Final`);
      });
      countHeaders.push("Difference", "Final");
    } else {
      countHeaders.push("Counted", "Difference", "Final");
    }

    const countRows = lines.map((line) => {
      const factor = Number(line.__conversionFactor || 1) || 1;
      const item = line.item || {};
      const hierarchy = buildProductGroup(item) || "-";
      const locationSummary = sessionHasLocationCounts ? getLineLocationSummary(line) : null;
      const expected = locationSummary
        ? locationSummary.expected
        : expectedInUnit(line, line.expected_qty, factor, countLevel(line), line.expected_exact_quantity);
      const currentValue =
        liveCounted[line.id] !== undefined ? liveCounted[line.id] : line.counted_qty ?? "";
      const countedNumber =
        currentValue === "" || currentValue === null ? null : Number(currentValue);

      const row = [
        hierarchy,
        item.code || "",
        item.name || "",
        getLineUnitLabel(line),
        blindMode ? "" : expected,
      ];

      if (sessionHasLocationCounts) {
        sessionCountLocations.forEach((location) => {
          const locationLine = getLineLocationLines(line).find(
            (entry) => Number(entry.location_id) === Number(location.id)
          );
          const locationValue = locationLine ? getLocationCountValue(line, location.id) : "";
          const initial = expectedInUnit(line, locationLine?.expected_qty, factor, countLevel(line), locationLine?.expected_exact_quantity);
          const finalValue =
            locationValue === "" || locationValue === null || locationValue === undefined
              ? initial
              : Number(locationValue);

          row.push(blindMode ? "" : initial, isBlankCountValue(locationValue) ? "" : Number(locationValue), blindMode || isBlankCountValue(locationValue) ? "" : countDifference(line, locationLine, locationValue, initial), hideDerivedCountFigures ? "" : finalValue);
        });

        row.push(
          blindMode || locationSummary.difference === null ? "" : locationSummary.difference,
          hideDerivedCountFigures ? "" : locationSummary.finalQty
        );
        return row;
      }

      const difference = countedNumber === null ? null : countDifference(line, line, currentValue, expected);
      row.push(
        countedNumber === null ? "" : countedNumber,
        blindMode || difference === null ? "" : difference,
        hideDerivedCountFigures || countedNumber === null ? "" : countedNumber
      );
      return row;
    });

    const workbook = XLSX.utils.book_new();
    const summarySheet = XLSX.utils.aoa_to_sheet(metadataRows);
    summarySheet["!cols"] = [{ wch: 32 }, { wch: 80 }];
    XLSX.utils.book_append_sheet(workbook, summarySheet, "Summary");

    const countSheet = XLSX.utils.aoa_to_sheet([countHeaders, ...countRows]);
    countSheet["!cols"] = countHeaders.map((header) => {
      if (header === "Item") return { wch: 42 };
      if (header === "Product Group") return { wch: 28 };
      if (header === "Code") return { wch: 18 };
      if (header.includes("Initial") || header.includes("Counted") || header.includes("Final")) return { wch: 15 };
      return { wch: 14 };
    });
    lines.forEach((line, index) => {
      if (!countBasis(line)) return;
      for (let column = 4; column < countHeaders.length; column++) {
        const cell = countSheet[XLSX.utils.encode_cell({ r: index + 1, c: column })];
        if (cell?.t === "n") cell.z = "0.####";
      }
    });
    XLSX.utils.book_append_sheet(workbook, countSheet, "Count");

    const safeDate = String(snapshotDate || today).replaceAll("-", "");
    const safeId = sessionId || "draft";
    XLSX.writeFile(workbook, `inventory_count_${safeId}_${safeDate}.xlsx`);
  };

  const renderSelectionChip = ({ id, label }) => (
    <span key={id} className="ic-filterTag ic-filterTag--location">
      {label}
      {!sessionId || canEditSession ? <button type="button" className="ic-filterRemove"
        disabled={structuralChangesBlocked} onClick={() => handleRemoveLocationChip(id)}
        aria-label={t(`Remove ${label}`)}>x</button> : null}
    </span>
  );

  const renderLocationsCard = () => (
    <div className="ic-selectionCard">
      <div className="ic-selectionCardHead">
        <div>
          <div className="ic-selectionTitle">{t("Locations")}</div>
          <div className="ic-selectionMeta">
            {locationFilterTags.length ? t(`${locationFilterTags.length} selected`) : t("No location selected")}
          </div>
        </div>
        {!sessionId || canEditSession ? (
          <button type="button" className="ic-miniAddBtn" disabled={structuralChangesBlocked} onClick={openLocationsEditor}>
            {t("Add")}
          </button>
        ) : null}
      </div>

      {locationFilterTags.length ? (
        <div className="ic-filterTagList">
          {locationFilterTags.map((entry) =>
            renderSelectionChip({
              id: entry.id,
              label: entry.label,
              kind: "location",
            })
          )}
        </div>
      ) : (
        <div className="ic-selectionEmpty">{t("Choose at least one location before generating the count.")}</div>
      )}
    </div>
  );

  const renderSelectionCards = () => (
    <div className="ic-selectionGrid ic-selectionGrid--locations">
      {renderLocationsCard()}
      {sessionId && canEditSession ? <button type="button" className="ic-miniAddBtn" disabled={structuralChangesBlocked}
        onClick={() => { if (!cannotChangeStructure()) setShowPastItemsPopup(true); }}>{t("Add item")}</button> : null}
    </div>
  );

  const renderLocationsEditorModal = () => (
    <div
      className="ic-dialogOverlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !structureBusy) {
          cancelLocationsEditor();
        }
      }}
    >
      <div className="ic-modal ic-modal--selectionEditor">
        <div className="ic-modalBody">
          <div className="ic-modalHeader">
            <div>
              <div className="ic-modalKicker">{t("Inventory Count")}</div>
              <h3 className="ic-modalTitle">{t("Locations")}</h3>
            </div>

            <div className="ic-modalFooterRight">
              <button type="button" className="ic-btn ic-btn--ghost" disabled={structureBusy} onClick={cancelLocationsEditor}>
                {t("Cancel")}
              </button>
              <button type="button" className="ic-btn ic-btn--primary" disabled={structureBusy} onClick={handleApplyLocations}>
                {structureBusy ? t("Applying...") : t("Apply")}
              </button>
            </div>
          </div>

          <input type="search" className="ic-input" aria-label={t("Find location")} placeholder={t("Find location")}
            value={locationSearch} onChange={(event) => setLocationSearch(event.target.value)} />
          <div className="ic-locationChoiceGrid">
            {selectableLocations.length ? (
              selectableLocations.filter((location) => `${location.name} ${location.code || ""}`.toLocaleLowerCase().includes(locationSearch.trim().toLocaleLowerCase())).map((location) => {
                const selected = selectedLocations.includes(Number(location.id));

                return (
                  <button
                    key={location.id}
                    type="button"
                    className={`ic-locationChoice ${selected ? "ic-locationChoice--active" : ""}`}
                    aria-pressed={selected}
                    disabled={structureBusy}
                    onClick={() =>
                      setSelectedLocations((prev) =>
                        selected
                          ? prev.filter((id) => Number(id) !== Number(location.id))
                          : Array.from(new Set([...prev, Number(location.id)]))
                      )
                    }
                  >
                    <span className="ic-locationChoiceName" title={location.name}>{location.name}</span>
                    <span className="ic-locationChoiceMeta" title={location.is_main ? t("Main Location") : location.code || t("Location")}>
                      {location.is_main ? t("Main Location") : location.code || t("Location")}
                    </span>
                  </button>
                );
              })
            ) : (
              <div className="ic-muted">{t("No inventory locations are available yet.")}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderStartRequirements = () => startContextError ? (
    <div className="ic-selectionEditorNote" role="alert">
      <p>{t("Count locations could not be checked. Starting a count is blocked until they can be verified.")}</p>
      <button type="button" className="ic-btn" onClick={() => setBootstrapRevision((revision) => revision + 1)}>{t("Retry")}</button>
    </div>
  ) : startContext?.locations?.length === 0 ? (
    <p className="ic-selectionEditorNote" role="status">{t("Select at least one accessible inventory location to count.")}</p>
  ) : null;

  const renderPilcoreSetupView = () => <>
    <header className="ic-modalHeader ic-countSetupHeader"><div><h2 className="ic-modalTitle">{t("Start Inventory Count")}</h2>
      <p className="ic-modalSubtitle">{t("Choose the date, locations and optional product groups. Only items with stock in the selected locations will be included.")}</p></div></header>
    {renderStartRequirements()}
    {startContext?.has_non_main_locations && startContext?.main_has_stock && <div className="ic-selectionEditorNote">
      <button type="button" className="ic-btn ic-btn--ghost" disabled={loading} onClick={() => setStartPolicy({
        ...startContext, can_start: true, code: "COUNT_MAIN_STOCK_AVAILABLE",
      })}>{t("Manage stock transfers")}</button>
    </div>}
    <section className="ic-countSetupStep"><h3><span>1</span>{t("Date and visibility")}</h3>
      <div className="ic-formGrid"><label className="ic-field"><span className="ic-label">{t("Snapshot Date")}</span>
        <input type="date" className="ic-input" value={snapshotDate} max={today} onChange={(event) => setSnapshotDate(event.target.value)} /></label>
        <label className={`ic-countSetupVisibility${blindMode ? " is-selected" : ""}`}>
          <input type="checkbox" checked={blindMode} onChange={(event) => setBlindMode(event.target.checked)} />
          <span><strong>{t("Blind count")}</strong><small>{t(blindMode ? "Expected quantities and derived totals stay hidden." : "Expected stock visible during count")}</small></span>
        </label></div>
    </section>
    <div className="ic-countSetupLocations"><section className="ic-countSetupStep"><h3><span>2</span>{t("Locations to count")}</h3>{renderLocationsCard()}</section>
    </div>
    <section className="ic-countSetupStep ic-countSetupScope"><h3><span>3</span>{t("Items to include")}</h3>
      <div className="ic-selectionCard"><div className="ic-selectionCardHead"><div>
        <div className="ic-selectionTitle">{t("Scope")}</div>
        <div className="ic-selectionMeta">{t("Optional product-group filters")}</div>
      </div><button type="button" className="ic-miniAddBtn" disabled={loading || !startContext || startContextError}
        onClick={() => setScopePickerOpen(true)}>{t("Add Scope")}</button></div>
        {Object.values(scopeFilters).some((ids) => ids.length) ? <div className="ic-filterTags">{Object.entries(scopeFilters).flatMap(([level, ids]) =>
          ids.map((id) => <span className="ic-filterTag" key={`${level}:${id}`}>
            {scopeCatalog[level].find((entry) => Number(entry.id) === Number(id))?.name || id}
          </span>))}</div> : <p className="ic-selectionEmpty">{t("All product groups with stock in the selected locations.")}</p>}
      </div>
    </section>
    <label className="ic-field ic-countSetupNotes"><span className="ic-label">{t("Notes")}</span>
      <input type="text" className="ic-input" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder={t("Optional guidance for the warehouse team...")} /></label>
    <footer className="ic-modalFooter ic-countSetupFooter"><div className="ic-muted"><strong>{selectedLocations.length}</strong> {t("Locations")}</div>
      <div className="ic-modalFooterRight"><button type="button" className="ic-btn ic-btn--ghost" disabled={loading} onClick={onClose}>{t("Cancel")}</button>
        <button type="button" className="ic-btn ic-btn--primary" disabled={loading || !startContext || startContextError} onClick={handleGenerate}>{t(loading ? "Generating..." : "Generate Count Items")}</button></div>
    </footer>
  </>;

  const renderSetupView = renderPilcoreSetupView;

  const renderMobileLocationSwitcher = () => {
    const currentValue = activeSessionLocationId || "";

    return (
      <div className="ic-mobileCountContext">
        <div className="ic-mobileCountContextMeta">
          <div className="ic-mobileSnapshotPill">
            <span>{t("Snapshot")}</span>
            <strong>{formatInventoryCountDate(snapshotDate)}</strong>
          </div>

          {sessionCountLocations.length > 4 ? (
            <details className="ic-mobileLocationPicker">
              <summary>{t("Location")}: {activeSessionLocation?.name}</summary>
              <input type="search" className="ic-input" aria-label={t("Find location")} value={locationSearch}
                onChange={(event) => setLocationSearch(event.target.value)} />
              <div className="ic-locationChoiceGrid">
                {sessionCountLocations.filter((location) => location.name.toLocaleLowerCase().includes(locationSearch.trim().toLocaleLowerCase())).map((location) => (
                  <button key={location.id} type="button" className="ic-locationChoice" aria-pressed={Number(location.id) === activeSessionLocationId}
                    onClick={(event) => { setActiveCountLocationId(Number(location.id)); event.currentTarget.closest("details").open = false; }}>
                    {location.name}
                  </button>
                ))}
              </div>
            </details>
          ) : sessionCountLocations.length ? (
            <label className="ic-mobileLocationCompact">
              <span>{t("Location")}</span>
              <select
                className="ic-select ic-mobileLocationSelect"
                value={currentValue}
                onChange={(event) => setActiveCountLocationId(Number(event.target.value))}
              >
                {sessionCountLocations.map((location) => {
                  const progress = pilcoreAppearance ? {
                    counted: lines.filter((line) => getLineLocationLines(line).some((entry) => Number(entry.location_id) === Number(location.id) && !isBlankCountValue(getLocationCountValue(line, location.id)))).length,
                    total: lines.filter((line) => getLineLocationLines(line).some((entry) => Number(entry.location_id) === Number(location.id))).length,
                  } : locationProgressById.get(Number(location.id)) || {
                    counted: 0,
                    total: 0,
                  };

                  return (
                    <option key={`mobile-location-select-${location.id}`} value={Number(location.id)}>
                      {location.name} ({progress.counted}/{progress.total})
                    </option>
                  );
                })}
              </select>
            </label>
          ) : null}
        </div>

        {canEditSession ? (
          <div className="ic-mobileQuickActions">
            {sessionId ? (
              <button
                type="button"
                className="ic-mobileQuickAction"
                disabled={structuralChangesBlocked}
                onClick={() => { if (!cannotChangeStructure()) setShowPastItemsPopup(true); }}
              >
                {t("Add item")}
              </button>
            ) : null}
            <button type="button" className="ic-mobileQuickAction" disabled={structuralChangesBlocked} onClick={openLocationsEditor}>
              {t("Add Location")}
            </button>
          </div>
        ) : null}
      </div>
    );
  };

  const getMobileCountLineMetrics = (line) => {
    const factor = Number(line.__conversionFactor || 1) || 1;
    const item = line.item || {};
    const hierarchy = buildProductGroup(item) || "-";
    const currentValue =
      liveCounted[line.id] !== undefined ? liveCounted[line.id] : line.counted_qty ?? "";
    const countedNumber =
      currentValue === "" || currentValue === null ? null : Number(currentValue);
    const activeLocationLine =
      sessionHasLocationCounts && activeSessionLocationId !== null
        ? getLineLocationLines(line).find(
            (entry) => Number(entry.location_id) === Number(activeSessionLocationId)
          )
        : null;
    const activeLocationValue = activeLocationLine
      ? getLocationCountValue(line, activeSessionLocationId)
      : "";
    const activeLocationBlank = isBlankCountValue(activeLocationValue);
    const expected = sessionHasLocationCounts
      ? expectedInUnit(line, activeLocationLine?.expected_qty, factor, countLevel(line), activeLocationLine?.expected_exact_quantity)
      : expectedInUnit(line, line.expected_qty, factor, countLevel(line), line.expected_exact_quantity);
    const finalQty = sessionHasLocationCounts
      ? activeLocationBlank
        ? expected
        : Number(activeLocationValue)
      : countedNumber;
    const difference = sessionHasLocationCounts
      ? activeLocationBlank
        ? null
        : countDifference(line, activeLocationLine, activeLocationValue, expected)
      : countedNumber === null
      ? null
      : countDifference(line, line, currentValue, expected);
    const diffClass =
      !blindMode && difference < 0
        ? "ic-diff--negative"
        : !blindMode && difference > 0
        ? "ic-diff--positive"
        : "";

    return {
      item,
      hierarchy,
      currentValue,
      activeLocationLine,
      activeLocationValue,
      activeLocationKey: activeLocationLine
        ? getLocationCountKey(line.id, activeLocationLine.location_id)
        : null,
      expected,
      finalQty,
      difference,
      diffClass,
    };
  };

  const renderMobileCountInput = (line, metrics) => {
    if (sessionHasLocationCounts) {
      return (
        <input
          type="number"
          inputMode="decimal"
          step="any"
          className="ic-input ic-mobileCountInput ic-number"
          value={liveLocationCounts[metrics.activeLocationKey] ?? countDisplay(line, metrics.activeLocationValue, countLevel(line), metrics.activeLocationLine?.counted_exact_quantity)}
          disabled={!canEditSession || !metrics.activeLocationLine || entryLocked}
          onChange={(event) => {
            if (!metrics.activeLocationKey) return;
            setLiveLocationCounts((prev) => ({
              ...prev,
              [metrics.activeLocationKey]: event.target.value,
            }));
          }}
          onBlur={(event) => {
            if (metrics.activeLocationLine && activeSessionLocationId !== null) {
              saveLocationCount(line, activeSessionLocationId, event.target.value);
            }
          }}
          aria-label={t(`Count ${metrics.item.name || "item"}`)}
        />
      );
    }

    return (
      <input
        type="number"
        inputMode="decimal"
        step="any"
        className="ic-input ic-mobileCountInput ic-number"
        value={liveCounted[line.id] ?? countDisplay(line, metrics.currentValue, countLevel(line), line.counted_exact_quantity)}
        disabled={!canEditSession || entryLocked}
        onChange={(event) =>
          setLiveCounted((prev) => ({
            ...prev,
            [line.id]: event.target.value,
          }))
        }
        onBlur={(event) => saveCount(line.id, event.target.value)}
        aria-label={t(`Count ${metrics.item.name || "item"}`)}
      />
    );
  };

  const renderMobileCountCard = (line) => {
    const metrics = getMobileCountLineMetrics(line);

    return (
      <article key={`mobile-count-${line.id}`} className="ic-mobileCountCard">
        <div className="ic-mobileCountHead">
          <div className="ic-mobileCountIdentity">
            <div className="ic-mobileCountMetaLine">
              <span className="ic-mobileCode">{metrics.item.code || "-"}</span>
              <span title={t(metrics.hierarchy)}>{metrics.hierarchy}</span>
            </div>
            <h3 className="ic-mobileCountTitle">{metrics.item.name || "-"}</h3>
            {renderChangedStock(line)}
          </div>

          {canEditSession ? (
            <button
              type="button"
              className="ic-mobileLineRemoveBtn"
              disabled={structuralChangesBlocked}
              onClick={() => handleDeleteLine(line)}
              title={t("Remove item from this count")}
              aria-label={t(`Remove ${metrics.item.name || "item"} from this count`)}
            >
              x
            </button>
          ) : null}
          {canCorrectFinalizedLines ? (
            <button
              type="button"
              className="ic-lineCorrectBtn ic-lineCorrectBtn--mobile"
              onClick={() => openLineCorrection(line)}
              title={t("Correct this finalized item")}
            >
              {t("Correct")}
            </button>
          ) : null}
        </div>

        <div className="ic-mobileCountControlRow">
          <div className="ic-mobileCountUnit">
            <span className="ic-mobileFieldLabel">{t("Unit")}</span>
            {canEditSession ? (
              <button
                type="button"
                className="ic-unitTrigger"
                disabled={structuralChangesBlocked}
                onClick={() => {
                  if (cannotChangeStructure()) return;
                  setUnitModalItem(line);
                  setShowUnitModal(true);
                }}
              >
                {getLineUnitLabel(line)}
              </button>
            ) : (
              <span className="ic-muted">{getLineUnitLabel(line)}</span>
            )}
          </div>

          <label className="ic-mobileCountCapture">
            <span className="ic-mobileFieldLabel">{t("Counted")}</span>
            {renderMobileCountInput(line, metrics)}
          </label>
        </div>

        <div className="ic-mobileCountStats">
          <div className="ic-mobileCountStat">
            <span className="ic-mobileFieldLabel">{t("Expected")}</span>
            <strong title={referenceTitle(metrics.activeLocationLine || line, line)}>{blindMode ? "-" : countNumber(line, metrics.expected)}</strong>
          </div>
          <div className="ic-mobileCountStat">
            <span className="ic-mobileFieldLabel">{t("Final")}</span>
            <strong>
              {hideDerivedCountFigures || metrics.finalQty === null ? "-" : countNumber(line, metrics.finalQty)}
            </strong>
          </div>
          <div className="ic-mobileCountStat">
            <span className="ic-mobileFieldLabel">{t("Difference")}</span>
            <strong className={metrics.diffClass}>
              {blindMode || metrics.difference === null
                ? "-"
                : countNumber(line, metrics.difference)}
            </strong>
          </div>

        </div>
      </article>
    );
  };

  const renderMobileCountList = () => (
    <div className="ic-mobileCountList">
      {visibleLines.length ? (
        visibleLines.map((line) => renderMobileCountCard(line))
      ) : (
        <div className="ic-empty">
          {lines.length && (normalizedCountSearch || (pilcoreAppearance && countViewFilter !== "all"))
            ? t(pilcoreAppearance ? "No count lines match these filters." : "No count lines match this search.")
            : t("This inventory count session has no lines yet.")}
        </div>
      )}
    </div>
  );

  const renderAddedLocationEmptyState = () => {
    if (!canEditSession) return null;
    const emptyLocations = sessionCountLocations.flatMap((location) => {
      if (!addedLocationIds.includes(Number(location.id))) return [];
      const locationLines = lines.flatMap((line) => getLineLocationLines(line)
        .filter((entry) => Number(entry.location_id) === Number(location.id))
        .map((entry) => ({ ...entry, countValue: getLocationCountValue(line, location.id) })));
      if (!locationLines.length) return [{ location, message: "No items are included in this location yet." }];
      // A zero-stock location still has count cells for every included item. Keep
      // those editable, and never reveal expected stock during a blind count.
      if (!blindMode && locationLines.every((entry) =>
        entry.expected_qty != null && Number(entry.expected_qty) === 0 &&
        (isBlankCountValue(entry.countValue) || Number(entry.countValue) === 0)
      )) return [{ location, message: "No stock is recorded for the items in this count yet. You can still enter quantities." }];
      return [];
    });
    if (!emptyLocations.length) return null;
    return <aside className="ic-locationEmptyState" role="status">
      {emptyLocations.map(({ location, message }) => <div key={location.id}>
        <strong>{location.name}</strong>{" · "}{t(message)}
      </div>)}
    </aside>;
  };

  const renderLineCorrectionModal = () => {
    if (!correctionLine || !canCorrectFinalizedLines) return null;

    const item = correctionLine.item || {};
    const locationLines = getLineLocationLines(correctionLine);
    const unitOptions = buildCorrectionUnitOptions(correctionLine);
    const activeCorrectionUnit =
      correctionUnit ||
      unitOptions.find((option) => option.key === correctionLine.__selectedUnit?.key) ||
      unitOptions[0] ||
      null;
    const factor = Number(activeCorrectionUnit?.factor || correctionLine.__conversionFactor || 1) || 1;
    const previousFinal = locationLines.length ? getLineLocationSummary(correctionLine).finalQty
      : isBlankCountValue(correctionLine.counted_qty) ? null : Number(correctionLine.counted_qty);
    const previousRecordedUnitLabel =
      correctionLine.__selectedUnit?.label || getLineUnitLabel(correctionLine);

    return (
      <div
        className="ic-dialogOverlay"
        onMouseDown={(event) => {
          if (!correctionBusy && event.target === event.currentTarget) {
            closeLineCorrection();
          }
        }}
      >
        <div className="ic-modal ic-modal--lineCorrection">
          <div className="ic-modalBody">
            <div className="ic-modalHeader">
              <div className="ic-modalHeaderMain">
                <div className="ic-modalKicker">{t("Inventory Count")}</div>
                <h3 className="ic-modalTitle">{t("Correct Item")}</h3>
                <p className="ic-modalSubtitle">
                  {item.code ? `${item.code} · ` : ""}
                  {item.name || t("Item")}
                </p>
              </div>
              <div className="ic-modalBadge">{activeCorrectionUnit?.label || getLineUnitLabel(correctionLine)}</div>
            </div>

            <div className="ic-correctionSummary">
              <div>
                <span>{t("Previous final")}</span>
                <strong>
                  {(hideDerivedCountFigures && locationLines.length) || previousFinal === null
                    ? "-"
                    : t(`${countNumber(correctionLine, previousFinal)} ${previousRecordedUnitLabel} (recorded)`)}
                </strong>
              </div>
              <div>
                <span>{t("Expected before count")}</span>
                <strong>
                  {hideDerivedCountFigures ? "—" : countNumber(correctionLine, expectedInUnit(correctionLine, locationLines.length ? locationLines.reduce((sum, row) => sum + Number(row.expected_qty || 0), 0) : correctionLine.expected_qty, factor, activeCorrectionUnit?.key), activeCorrectionUnit?.key)}
                </strong>
              </div>
            </div>

            <div className="ic-correctionUnitPicker">
              <span className="ic-label">{t("Correct unit")}</span>
              <div className="ic-correctionUnitOptions">
                {unitOptions.map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className={`ic-correctionUnitOption ${
                      activeCorrectionUnit?.key === option.key ? "is-active" : ""
                    }`}
                    disabled={correctionBusy}
                    onClick={() => selectCorrectionUnit(option)}
                    title={t(option.helper)}
                  >
                    <strong>{option.label}</strong>
                    <small>{option.key === "unit" ? t("Inventory unit") : t(option.helper)}</small>
                  </button>
                ))}
              </div>
            </div>

            {locationLines.length ? (
              <div className="ic-correctionLocationList">
                {locationLines.map((locationLine) => {
                  const key = getLocationCountKey(correctionLine.id, locationLine.location_id);
                  const expected = expectedInUnit(correctionLine, locationLine.expected_qty, factor, activeCorrectionUnit?.key);

                  return (
                    <label key={key} className="ic-correctionLocationRow">
                      <span>
                        <strong>{locationLine.location?.name || t("Location")}</strong>
                        <small>{t("Expected")} {hideDerivedCountFigures ? "—" : countNumber(correctionLine, expected, activeCorrectionUnit?.key)}</small>
                      </span>
                      <input
                        type="number"
                        min="0"
                        step="any"
                        inputMode="decimal"
                        className="ic-input ic-number"
                        value={correctionEditKey === key ? correctionValues[key] : countDisplay(correctionLine, correctionValues[key] ?? "", activeCorrectionUnit?.key)}
                        onBlur={() => setCorrectionEditKey(null)}
                        disabled={correctionBusy}
                        onChange={(event) => {
                          setCorrectionEditKey(key);
                          setCorrectionExact((prev) => ({ ...prev, [key]: exactInput(event.target.value, activeCorrectionUnit?.key, countBasis(correctionLine)) }));
                          setCorrectionValues((prev) => ({ ...prev, [key]: event.target.value }));
                        }}
                      />
                    </label>
                  );
                })}
              </div>
            ) : (
              <label className="ic-field">
                <span className="ic-label">{t("Correct final quantity")}</span>
                <input
                  type="number"
                  min="0"
                  step="any"
                  inputMode="decimal"
                  className="ic-input ic-number"
                  value={correctionEditKey === "counted_qty" ? correctionValues.counted_qty : countDisplay(correctionLine, correctionValues.counted_qty ?? "", activeCorrectionUnit?.key)}
                  onBlur={() => setCorrectionEditKey(null)}
                  disabled={correctionBusy}
                  onChange={(event) => {
                    setCorrectionEditKey("counted_qty");
                    setCorrectionExact((prev) => ({ ...prev, counted_qty: exactInput(event.target.value, activeCorrectionUnit?.key, countBasis(correctionLine)) }));
                    setCorrectionValues((prev) => ({ ...prev, counted_qty: event.target.value }));
                  }}
                />
              </label>
            )}

            <label className="ic-field">
              <span className="ic-label">{t("Reason")}</span>
              <textarea
                className="ic-input ic-correctionReason"
                value={correctionReason}
                disabled={correctionBusy}
                onChange={(event) => setCorrectionReason(event.target.value)}
                placeholder={t("Short audit note...")}
              />
            </label>

            <div className="ic-modalFooter">
              <div className="ic-modalFooterRight">
                <button type="button" className="ic-btn ic-btn--ghost" disabled={correctionBusy} onClick={closeLineCorrection}>
                  {t("Cancel")}
                </button>
                <button
                  type="button"
                  className="ic-btn ic-btn--warning"
                  disabled={correctionBusy}
                  onClick={() => submitLineCorrection()}
                >
                  {correctionBusy ? t("Saving...") : t("Save Correction")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderCompactSessionChrome = () => <>
    <header className="ic-countHeader">
      <h2 className="ic-modalTitle">{t("Inventory Count #")}{sessionId}</h2>
      <div className="ic-countIdentity">
        {canEditSession ? <label>{t("Snapshot Date")}: <input type="date" className="ic-input"
          aria-label={t("Snapshot Date")} value={snapshotDate} max={today}
          disabled={closeSaveBusy || structureBusy || finalizationBusy || confirmBusy || loading}
          onChange={(event) => setSnapshotDate(event.target.value)} onBlur={saveSessionDate} /></label>
          : <span>{t("Snapshot Date")}: <strong>{formatInventoryCountDate(snapshotDate)}</strong></span>}
        <span className={`ic-status ic-status--${sessionStatusMeta.tone}`}>{t(sessionStatusMeta.label)}</span>
        <span className="ic-countVisibility">{t(blindMode ? "Blind count" : "Visible expected")}</span>
        {renderSessionActions()}
      </div>
      <button type="button" className="ic-btn ic-btn--ghost ic-countClose"
        aria-label={t(canEditSession ? "Save and close" : "Close")}
        title={t(canEditSession ? "Save and close" : "Close")} aria-busy={closeSaveBusy}
        disabled={closeSaveBusy || Boolean(workbookAction) || loading || structureBusy || confirmBusy ||
          finalizationBusy || correctionBusy || Boolean(addingPastItemId) || refreshStatus === "loading"}
        onClick={saveAndCloseWorkspace}><FiX size={18} aria-hidden="true" /></button>
    </header>

    <div className="ic-countTools">
      <label className="ic-countSearch">
        <span className="ic-countSrOnly">{t("Search")}</span>
        <input type="search" className="ic-input" value={countSearch} onChange={(event) => setCountSearch(event.target.value)} placeholder={t("Filter items, codes, groups...")} />
      </label>
      <label className="ic-countViewFilter"><span>{t("Show")}</span>
        <select className="ic-select" value={countViewFilter} onChange={(event) => setCountViewFilter(event.target.value)}>
          <option value="all">{t("All lines")}</option><option value="blank">{t("With blank cells")}</option>
          {!blindMode && <option value="differences">{t("With differences")}</option>}
        </select>
      </label>
      {renderExpectedRefresh()}
      <span className="ic-countVisibleLines">{t("Visible lines")}: <strong>{visibleLines.length}</strong> / {lines.length}</span>
    </div>

    <div className="ic-countProgress" aria-label={t("Count progress")}>
      <span>{t("Entered cells")}: <strong>{captureProgress.filled} / {captureProgress.total}</strong></span>
      <progress max={Math.max(1, captureProgress.total)} value={captureProgress.filled} aria-label={t("Entered cells")} />
      <span>{t("Blank cells")}: <strong>{captureProgress.blanks}</strong></span>
      {!blindMode && <span>{t("Differences")}: <strong>{sessionStats.differenceLines}</strong></span>}
      <span className="ic-countBlankHint">{t("Blank is not zero. Enter 0 to confirm none.")}</span>
    </div>

    <details className="ic-countDetails">
      <summary>{t("Locations and notes")}<span>{locationFilterTags.length} {t("Locations")}{notes ? ` · ${t("Notes added")}` : ""}</span></summary>
      <div className="ic-countDetailsBody">
        {renderSelectionCards()}
        <label className="ic-field ic-countNotes"><span className="ic-label">{t("Notes")}</span>
          <input type="text" className="ic-input" value={notes} disabled={!canEditSession || entryLocked}
            onChange={(event) => setNotes(event.target.value)} onBlur={saveSessionNotes} placeholder={t("Optional guidance for the warehouse team...")} />
        </label>
      </div>
    </details>
    {sessionCountLocations.length > 0 && renderMobileLocationSwitcher()}
    {renderAddedLocationEmptyState()}
  </>;

  const renderCompactSessionFooter = () => <footer className="ic-countFooter">
    <div className={`ic-countSaveStatus ic-countSaveStatus--${countSaveState}`} role={countSaveState === "error" ? "alert" : "status"}>
      <span>{t(!canEditSession ? (canCorrectFinalizedLines ? "Finalized snapshot. Use Correct to record an audited correction." : isCounting ? "Uploading a count requires permission to edit inventory counts." : "Snapshot available for review only.") : countSaveState === "saving" ? "Saving entries..." : countSaveState === "error" ? "Some entries were not saved. Your values are still in the fields." : hasUnsavedInputs ? "Changes pending. Leave the field to save." : countSaveState === "saved" ? "All changes saved." : "Enter a quantity, then leave the cell to save.")}</span>
      {countSaveState === "error" && <button type="button" className="ic-btn" onClick={retryFailedCountSaves}>{t("Retry saving")}</button>}
    </div>
  </footer>;

  const renderSessionView = () => (
    <>
      {pilcoreAppearance ? renderCompactSessionChrome() : <>
      <div className="ic-modalHeader">
        <div className="ic-modalHeaderMain">
          <div className="ic-modalKicker">{t("Inventory Count")}</div>
          <h2 className="ic-modalTitle">{t("Inventory Count #")}{sessionId}</h2>
          {renderExpectedRefresh()}
          <p className="ic-modalSubtitle">
            {t("Capture live counts, switch unit views when needed and keep a clean warehouse sheet from opening to final review.")}
          </p>
        </div>

        <div className="ic-modalHeaderActions">
          {renderSessionActions()}
        </div>
      </div>

      <div className="ic-summaryBar">
        <div className="ic-summaryItem">
          <div className="ic-summaryItemLabel">{t("Status")}</div>
          <div className="ic-summaryItemValue">
            <span className={`ic-status ic-status--${sessionStatusMeta.tone}`}>
              {t(sessionStatusMeta.label)}
            </span>
          </div>
        </div>
        <div className="ic-summaryItem">
          <div className="ic-summaryItemLabel">{t("Snapshot Date")}</div>
          <div className="ic-summaryItemValue">{formatInventoryCountDate(snapshotDate)}</div>
        </div>
        <div className="ic-summaryItem">
          <div className="ic-summaryItemLabel">{t("Counted Lines")}</div>
          <div className="ic-summaryItemValue">
            {sessionStats.countedLines} / {sessionStats.totalLines}
          </div>
        </div>
        <div className="ic-summaryItem">
          <div className="ic-summaryItemLabel">{t("Differences")}</div>
          <div className="ic-summaryItemValue">
            {blindMode ? t("Hidden in blind mode") : sessionStats.differenceLines}
          </div>
        </div>
        <div className="ic-summaryItem">
          <div className="ic-summaryItemLabel">{t("Visibility")}</div>
          <div className="ic-summaryItemValue">{t(sessionStats.blindMode)}</div>
        </div>
      </div>

      {renderSelectionCards()}

      {renderMobileLocationSwitcher()}

      <div className="ic-sessionUtilityRow">
        <div className="ic-field ic-notesField">
          <label className="ic-label">{t("Notes")}</label>
          <input
            type="text"
            className="ic-input ic-input--notesLine"
            value={notes}
            disabled={!canEditSession || entryLocked}
            onChange={(event) => setNotes(event.target.value)}
            onBlur={saveSessionNotes}
            placeholder={t("Optional guidance for the warehouse team...")}
          />
        </div>
        <div className="ic-field ic-countSearchField">
          <label className="ic-label">{t("Search")}</label>
          <div className="ic-countSearchControl">
            <input
              type="search"
              className="ic-input ic-input--notesLine"
              value={countSearch}
              onChange={(event) => setCountSearch(event.target.value)}
              placeholder={t("Filter items, codes, groups...")}
            />
            {countSearch ? (
              <button
                type="button"
                className="ic-countSearchClear"
                onClick={() => setCountSearch("")}
                aria-label={t("Clear search")}
              >
                x
              </button>
            ) : null}
          </div>
        </div>
      </div>

      </>}
      <div className="ic-tableWrap ic-tableWrap--modal ic-tableWrap--sessionModal ic-locationScrollSyncScope">
        <table
          className={`ic-table ic-table--session ${
            sessionHasLocationCounts ? "ic-table--sessionLocation" : "ic-table--sessionLegacy"
          }`}
          style={{
            "--ic-location-count": sessionCountLocations.length,
            "--ic-location-column-width": locationColumnLayout.columnWidth,
            "--ic-location-window-width": locationColumnLayout.windowWidth,
            "--ic-location-strip-min-width": locationColumnLayout.stripMinWidth,
          }}
        >
          <colgroup>
            <col style={{ width: sessionHasLocationCounts ? "132px" : "17%" }} />
            <col style={{ width: sessionHasLocationCounts ? "82px" : "9%" }} />
            <col style={{ width: sessionHasLocationCounts ? "250px" : "26%" }} />
            <col style={{ width: sessionHasLocationCounts ? "74px" : "10%" }} />
            <col style={{ width: sessionHasLocationCounts ? "88px" : "9%" }} />
            <col style={{ width: sessionHasLocationCounts ? "var(--ic-location-window-width)" : "10%" }} />
            <col style={{ width: sessionHasLocationCounts ? "92px" : "10%" }} />
            <col style={{ width: sessionHasLocationCounts ? "116px" : "10%" }} />
            {canCorrectFinalizedLines ? <col style={{ width: "82px" }} /> : null}
          </colgroup>
          <thead>
            <tr>
              <th>{t("Product Group")}</th>
              <th>{t("Code")}</th>
              <th>{t("Item")}</th>
              <th>{t("Unit")}</th>
              <th className="ic-number">{t("Expected")}</th>
              {sessionHasLocationCounts ? (
                <th className="ic-locationWindowCell">
                  <div className="ic-locationScroll ic-locationScroll--head" onScroll={syncLocationScroll}>
                    <div className="ic-locationStrip">
                      {sessionCountLocations.map((location) => (
                        <div key={`head-location-${location.id}`} className="ic-locationHeadCell">
                          {location.name}
                        </div>
                      ))}
                    </div>
                  </div>
                </th>
              ) : (
                <th className="ic-number">{t("Counted")}</th>
              )}
              <th className="ic-number">{t("Difference")}</th>
              <th className="ic-number">{t("Final")}</th>
              {canCorrectFinalizedLines ? <th>{t("Correct")}</th> : null}
            </tr>
          </thead>
          <tbody>
            {visibleLines.length ? (
              visibleLines.map((line) => {
                const factor = line.__conversionFactor || 1;
                const currentValue =
                  liveCounted[line.id] !== undefined ? liveCounted[line.id] : line.counted_qty ?? "";
                const countedNumber =
                  currentValue === "" || currentValue === null ? null : Number(currentValue);
                const locationSummary = getLineLocationSummary(line);
                const expected = sessionHasLocationCounts
                  ? locationSummary.expected
                  : expectedInUnit(line, line.expected_qty, factor, countLevel(line), line.expected_exact_quantity);
                const difference = sessionHasLocationCounts
                  ? locationSummary.difference
                  : countedNumber === null
                  ? null
                  : countDifference(line, line, currentValue, expected);
                const finalQty = sessionHasLocationCounts ? locationSummary.finalQty : countedNumber;
                const item = line.item || {};
                const hierarchy = buildProductGroup(item) || "-";
                const diffClass =
                  !blindMode && difference < 0
                    ? "ic-diff--negative"
                    : !blindMode && difference > 0
                    ? "ic-diff--positive"
                    : "";

                return (
                  <tr key={line.id}>
                    <td className="ic-muted" title={t(hierarchy)}>
                      <span className="ic-productGroupText">
                        {hierarchy}
                      </span>
                    </td>
                    <td className="ic-muted">{item.code || "-"}</td>
                    <td className={`ic-itemCell ${canEditSession ? "ic-itemCell--editable" : ""}`} title={t(item.name || "-")}>
                      <span className="ic-strong ic-ellipsis">
                        {truncateInventoryCountText(item.name || "-", 65)}
                      </span>
                      {renderChangedStock(line)}
                      {canEditSession ? (
                        <button
                          type="button"
                          className="ic-lineRemoveBtn"
                          disabled={structuralChangesBlocked}
                          onClick={() => handleDeleteLine(line)}
                          title={t("Remove item from this count")}
                          aria-label={t(`Remove ${item.name || "item"} from this count`)}
                        >
                          x
                        </button>
                      ) : null}
                    </td>
                    <td>
                      {canEditSession ? (
                        <button
                          type="button"
                          className="ic-unitTrigger"
                          disabled={structuralChangesBlocked}
                          onClick={() => {
                            if (cannotChangeStructure()) return;
                            setUnitModalItem(line);
                            setShowUnitModal(true);
                          }}
                        >
                          {getLineUnitLabel(line)}
                        </button>
                      ) : (
                        <span className="ic-muted">{getLineUnitLabel(line)}</span>
                      )}
                    </td>
                    <td className="ic-number" title={referenceTitle(line, line)}>{blindMode ? "-" : countNumber(line, expected)}</td>
                    {sessionHasLocationCounts ? (
                      <td className="ic-locationWindowCell ic-locationWindowCell--body">
                        <div className="ic-locationScroll" onScroll={syncLocationScroll}>
                          <div className="ic-locationStrip">
                            {sessionCountLocations.map((location) => {
                              const locationLine = getLineLocationLines(line).find(
                                (entry) => Number(entry.location_id) === Number(location.id)
                              );
                              const locationValue = locationLine
                                ? getLocationCountValue(line, location.id)
                                : "";
                              const initial = expectedInUnit(line, locationLine?.expected_qty, factor, countLevel(line), locationLine?.expected_exact_quantity);
                              const finalValue =
                                locationValue === "" || locationValue === null || locationValue === undefined
                                  ? initial
                                  : Number(locationValue);
                              const key = getLocationCountKey(line.id, location.id);

                              return (
                                <div
                                  key={`${line.id}-${location.id}`}
                                  className="ic-number ic-locationCountCell ic-locationCountCell--strip"
                                >
                                  <input
                                    type="number"
                                    inputMode="decimal"
                                    placeholder="—"
                                    step="any"
                                    className="ic-input ic-inlineInput ic-number"
                                    aria-label={`${t("Counted")}: ${item.name || item.code || line.id} · ${location.name}`}
                                    value={liveLocationCounts[key] ?? countDisplay(line, locationValue, countLevel(line), locationLine?.counted_exact_quantity)}
                                    disabled={!canEditSession || !locationLine || entryLocked}
                                    onChange={(event) =>
                                      setLiveLocationCounts((prev) => ({
                                        ...prev,
                                        [key]: event.target.value,
                                      }))
                                    }
                                    onBlur={(event) => saveLocationCount(line, location.id, event.target.value)}
                                  />
                                  <div className="ic-countCorners">
                                    <span title={referenceTitle(locationLine, line)}>{isCounting ? `${t("Expected")} ${blindMode ? "—" : countNumber(line, initial)}`
                                      : blindMode ? t("Initial -") : t(`Initial ${countNumber(line, initial)}`)}</span>
                                    <span>{blindMode || isBlankCountValue(locationValue) ? `${t("Difference")} —` : `${t("Difference")} ${countNumber(line, countDifference(line, locationLine, locationValue, initial))}`}</span>
                                    <span>
                                      {hideDerivedCountFigures ? `${t("Final")} —` : `${t("Final")} ${countNumber(line, finalValue)}`}
                                    </span>
                                  </div>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </td>
                    ) : (
                      <td className="ic-number">
                        <input
                          type="number"
                          inputMode="decimal"
                          placeholder="—"
                          step="any"
                          className="ic-input ic-inlineInput ic-number"
                          aria-label={`${t("Counted")}: ${item.name || item.code || line.id}`}
                          value={liveCounted[line.id] ?? countDisplay(line, currentValue, countLevel(line), line.counted_exact_quantity)}
                          disabled={!canEditSession || entryLocked}
                          onChange={(event) =>
                            setLiveCounted((prev) => ({
                              ...prev,
                              [line.id]: event.target.value,
                            }))
                          }
                          onBlur={(event) => saveCount(line.id, event.target.value)}
                        />
                      </td>
                    )}
                    <td className={`ic-number ${diffClass}`}>
                      {blindMode
                        ? "-"
                        : difference === null
                        ? "-"
                        : countNumber(line, difference)}
                    </td>
                    <td className="ic-number ic-finalQuantityCell">
                      {hideDerivedCountFigures || finalQty === null ? "-" : countNumber(line, finalQty)}
                    </td>
                    {canCorrectFinalizedLines ? (
                      <td className="ic-rowActionCell">
                        <button
                          type="button"
                          className="ic-lineCorrectBtn"
                          onClick={() => openLineCorrection(line)}
                          title={t("Correct this finalized item")}
                        >
                          {t("Correct")}
                        </button>
                      </td>
                    ) : null}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td
                  colSpan={8 + (canCorrectFinalizedLines ? 1 : 0)}
                  className="ic-empty"
                >
                  {lines.length && (normalizedCountSearch || (pilcoreAppearance && countViewFilter !== "all"))
                    ? t(pilcoreAppearance ? "No count lines match these filters." : "No count lines match this search.")
                    : t("This inventory count session has no lines yet.")}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {renderMobileCountList()}
      {pilcoreAppearance && renderCompactSessionFooter()}
    </>
  );

  closeTopDialogRef.current = () => {
    if (closeSavePendingRef.current || workbookAction || confirmBusy || finalizationBusy || correctionBusy || structureBusy || addingPastItemId || (pilcoreAppearance && pendingCountSavesRef.current)) return;
    if (noticeAction) dismissNotice(false);
    else if (finalizationReview) returnFromFinalReview();
    else if (confirmAction) setConfirmAction(null);
    else if (correctionLine) closeLineCorrection();
    else if (showUnitModal) setShowUnitModal(false);
    else if (showPastItemsPopup) setShowPastItemsPopup(false);
    else if (locationsEditOpen) cancelLocationsEditor();
    else closeWorkspace();
  };

  useEffect(() => {
    if (!pilcoreAppearance || !isOpen || !appearanceRef.current || startPolicy || scopePickerOpen) return undefined;
    const panels = appearanceRef.current.querySelectorAll(".ic-modal, .ic-dialog");
    const panel = panels[panels.length - 1];
    if (!panel) return undefined;
    const previousFocus = document.activeElement;
    panel.setAttribute("role", panel.classList.contains("ic-dialog") ? "alertdialog" : "dialog");
    panel.setAttribute("aria-modal", "true");
    panel.setAttribute("aria-label", panel.querySelector(".ic-modalTitle, .ic-dialogTitle")?.textContent || t("Inventory Count"));
    panel.tabIndex = -1;
    panel.focus({ preventScroll: true });
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopImmediatePropagation();
        closeTopDialogRef.current?.();
      } else if (event.key === "Tab") {
        const controls = Array.from(panel.querySelectorAll("button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), summary, [tabindex='0']"))
          .filter((element) => {
            const closedDetails = element.closest("details:not([open])");
            if (closedDetails && element !== closedDetails.querySelector("summary")) return false;
            for (let node = element; node && node !== panel; node = node.parentElement) {
              const style = getComputedStyle(node);
              if (node.hidden || style.display === "none" || style.visibility === "hidden") return false;
            }
            return true;
          });
        const first = controls[0];
        const last = controls[controls.length - 1];
        if (!first) { event.preventDefault(); panel.focus(); }
        else if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && (document.activeElement === last || !panel.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (previousFocus?.isConnected) previousFocus.focus({ preventScroll: true });
    };
  }, [pilcoreAppearance, isOpen, contentReady, locationsEditOpen, showPastItemsPopup, showUnitModal, correctionLine, confirmAction, noticeAction, finalizationReview, startPolicy, scopePickerOpen, t]);

  if (!isOpen) return null;
  if (!can("view") || (!sessionIdFromList && !sessionId && !can("create"))) return <div role="alert">{uiLanguage === "es" ? "No tienes permiso para abrir este recuento de inventario." : "You do not have permission to open this inventory count."}<button type="button" onClick={onClose}>{t("Close")}</button></div>;

  return (
    <div ref={appearanceRef} className={pilcoreAppearance ? "pilcore-purchasing-overlay ic-pilcore" : undefined} data-theme={pilcoreAppearance ? uiTheme : undefined}>
      <style>{INVENTORY_COUNT_UI_STYLES}</style>

      <div className={`ic-modalOverlay ${sessionId ? "ic-modalOverlay--inventorySession" : ""}`}>
        <div className={`ic-modal ${sessionId ? "ic-modal--wide ic-modal--inventorySession" : ""}`}>
          <div className="ic-modalBody">
            {!contentReady ? (
              <div className="ic-empty">{t("Loading inventory count workspace...")}</div>
            ) : refreshRecovery !== null ? (
              <section className="ic-refreshRecovery" role="alert">
                <p>{t("This count changed in another session. Reopen it to see the current count.")}</p>
                {refreshRecovery && <label>{t("Copy your entries before closing.")}
                  <textarea className="ic-input" readOnly value={refreshRecovery} />
                </label>}
                <button type="button" className="ic-btn" onClick={closeWorkspace}>{t("Close")}</button>
              </section>
            ) : sessionLoadError ? (
              <div className="ic-empty" role="alert">
                <div>{t(sessionLoadError)}</div>
                <div className="ic-modalFooterRight" style={{ marginTop: "14px" }}>
                  <button type="button" className="ic-btn ic-btn--ghost" onClick={onClose}>
                    {t("Close")}
                  </button>
                  <button
                    type="button"
                    className="ic-btn ic-btn--primary"
                    onClick={() => sessionId ? refreshImportedSession(countSaveGenerationRef.current) : setBootstrapRevision((revision) => revision + 1)}
                  >
                    {t("Retry")}
                  </button>
                </div>
              </div>
            ) : sessionId ? (
              renderSessionView()
            ) : (
              renderSetupView()
            )}
          </div>
        </div>
      </div>

      {locationsEditOpen ? renderLocationsEditorModal() : null}
      {locationFeedback && <div className="ic-locationToast" role="status" aria-live="polite">
        <strong>{t(locationFeedback.message)}</strong>
        <span>{locationFeedback.names}</span>
      </div>}

      {showPastItemsPopup ? (
        <div
          className="ic-dialogOverlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget && !addingPastItemId) {
              setShowPastItemsPopup(false);
            }
          }}
        >
          <div className="ic-modal">
            <div className="ic-modalBody">
              <div className="ic-modalHeader">
                <div>
                  <div className="ic-modalKicker">{t("Inventory Count")}</div>
                  <h3 className="ic-modalTitle">{t("Add item")}</h3>
                  <p className="ic-modalSubtitle">
                    {t("Add inventory items found in the selected locations, including items with no recorded stock.")}
                  </p>
                </div>

                <div className="ic-modalBadge">{t("Inventory items")}</div>
              </div>

              <div className="ic-toolbar" style={{ paddingLeft: 0, paddingRight: 0, background: "transparent" }}>
                <div className="ic-toolbarMain">
                  <input
                    type="search"
                    className="ic-search"
                    value={pastItemsSearch}
                    placeholder={t("Search by item, code or hierarchy...")}
                    onChange={(event) => setPastItemsSearch(event.target.value)}
                  />
                </div>
              </div>

              <div className="ic-tableWrap ic-tableWrap--modal">
                <table className="ic-table ic-table--pastItems">
                  <thead>
                    <tr>
                      <th>{t("Item")}</th>
                      <th>{t("Code")}</th>
                      <th>{t("Product Group")}</th>
                      <th style={{ textAlign: "right" }}>{t("Action")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visiblePastItems.length ? (
                      visiblePastItems.map((item) => {
                        const hierarchy = buildProductGroup(item) || "-";

                        return (
                          <tr key={item.id}>
                            <td title={t(item.name)}>
                              <span className="ic-strong ic-ellipsis" style={{ maxWidth: "220px" }}>
                                {truncateInventoryCountText(item.name, 70)}
                              </span>
                              {item.__removedFromCount ? (
                                <span className="ic-restoreBadge">{t("Removed")}</span>
                              ) : null}
                            </td>
                            <td className="ic-muted">{item.code || "-"}</td>
                            <td className="ic-muted" title={t(hierarchy)}>
                              <span className="ic-ellipsis" style={{ maxWidth: "320px" }}>
                                {hierarchy}
                              </span>
                            </td>
                            <td>
                              <div className="ic-actions">
                                <button
                                  type="button"
                                  className="ic-btn ic-btn--primary"
                                  disabled={addingPastItemId === item.id || structuralChangesBlocked}
                                  onClick={() => handleAddPastItem(item.id)}
                                >
                                  {addingPastItemId === item.id ? t("Adding...") : t("Add Item")}
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    ) : (
                      <tr>
                        <td colSpan="4" className="ic-empty">
                          {t("No inventory items match the search.")}
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <div className="ic-modalFooter">
                <div className="ic-muted">
                  {t("Choose individual items to append them directly to the active inventory count.")}
                </div>
                <div className="ic-modalFooterRight">
                  <button
                    type="button"
                    className="ic-btn ic-btn--ghost"
                    onClick={() => setShowPastItemsPopup(false)}
                    disabled={Boolean(addingPastItemId)}
                  >
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showUnitModal && unitModalItem ? (
        <div
          className="ic-dialogOverlay"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) {
              setShowUnitModal(false);
            }
          }}
        >
          <div className="ic-modal" style={{ width: "min(520px, 94vw)" }}>
            <div className="ic-modalBody">
              <div className="ic-modalHeader">
                <div>
                  <div className="ic-modalKicker">{t("Inventory Count")}</div>
                  <h3 className="ic-modalTitle">{t("Select Count Unit")}</h3>
                  <p className="ic-modalSubtitle">
                    {t("Choose how this item should be counted so expected and difference values stay aligned with the warehouse reality.")}
                  </p>
                </div>

                <div className="ic-modalBadge">{truncateInventoryCountText(unitModalItem.item?.name || "-", 32)}</div>
              </div>

              <div className="ic-choiceList">
                {buildUnitOptions(unitModalItem).map((option) => (
                  <button
                    key={option.key}
                    type="button"
                    className="ic-choiceButton"
                    disabled={structuralChangesBlocked}
                    aria-pressed={option.key === unitModalItem.__selectedUnit?.key}
                    onClick={() => handleSelectCountUnit(unitModalItem, option)}
                  >
                    <div>
                      <div className="ic-choicePrimary">{option.label}</div>
                      <div className="ic-choiceMeta">{t(option.helper)}</div>
                    </div>
                    <span className="ic-badge">x{formatInventoryCountNumber(option.factor)}</span>
                  </button>
                ))}
              </div>

              <div className="ic-modalFooter">
                <div className="ic-muted">
                  {t("Current unit:")} {getLineUnitLabel(unitModalItem)}
                </div>
                <div className="ic-modalFooterRight">
                  <button type="button" className="ic-btn ic-btn--ghost" onClick={() => setShowUnitModal(false)}>
                    {t("Close")}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {renderLineCorrectionModal()}

      {scopePickerOpen && <InventoryCountScopePicker {...scopeCatalog} value={scopeFilters} language={uiLanguage}
        onApply={(filters) => { setScopeFilters(filters); setScopePickerOpen(false); }} onClose={() => setScopePickerOpen(false)} />}

      {startPolicy && <InventoryCountStartGuard
        policy={startPolicy}
        businessUnitId={businessUnitId}
        language={uiLanguage}
        busy={loading}
        onRecheck={startPolicy.code === "COUNT_MAIN_STOCK_AVAILABLE" ? reloadStartContextAfterTransfer : recheckStartPolicy}
        onClose={() => { if (!loading) setStartPolicy(null); }}
      />}

      {finalizationReview && <InventoryCountFinalizationReview
        preview={finalizationReview.preview}
        payload={finalizationReview.payload}
        language={uiLanguage}
        blind={blindMode}
        busy={finalizationBusy}
        error={finalizationReview.error}
        errorDetails={finalizationReview.errorDetails}
        onReview={refreshFinalizationReview}
        onFinalize={finalizeReviewedCount}
        onClose={returnFromFinalReview}
      />}

      <InventoryCountConfirmDialog
        t={t}
        action={confirmAction}
        busy={confirmBusy}
        onCancel={() => {
          if (!confirmBusy) {
            setConfirmAction(null);
          }
        }}
        onConfirm={handleConfirmAction}
      />
      <InventoryCountConfirmDialog
        t={t}
        action={noticeAction}
        busy={false}
        onCancel={() => dismissNotice(false)}
        onConfirm={() => dismissNotice(true)}
      />
    </div>
  );
}
